import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  X,
  Upload,
  User,
  Mail,
  Phone,
  Calendar,
  Building,
  MapPin,
  CreditCard,
  FileText,
  AlertCircle,
  CheckCircle,
  Shield,
  Search,
  Download,
  Plus,
  Trash2
} from 'lucide-react';
import PermissionGate from '../../../components/common/PermissionGate';
import hrService from '../../../api/services/hrService';
import { documentTypeService, employmentTypeService, employeeTeamService } from '../../../api/crm/config/configService';
import locationService from '../../../api/services/locationService';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';
import { post, del } from '../../../api/apiClient';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { useAuth } from '../../../context/AuthContext';
import { ROLE_HIERARCHY, ROLE_ASSIGNMENT_HIERARCHY, USER_ROLES } from '../../../utils/constants';
import { API_BASE_URL, API_ENDPOINTS } from '../../../api/config';
import { validateField, validateEmployeeForm } from '../../../utils/employeeValidation';
import { INVALID_CHARS_REGEX } from '../../../utils/formValidation';
import { toast } from 'react-hot-toast';

const AddEmployee = ({ id, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { employeeId: paramEmployeeId } = useParams(); // Get employeeId from URL if editing
  const editEmployeeId = id || paramEmployeeId;
  const { user } = useAuth();

  // Toast helpers (react-hot-toast)
  const showSuccess = (message, title = '') => {
    toast.success(title ? `${title}: ${message}` : message);
  };

  const showError = (message, title = '') => {
    toast.error(title ? `${title}: ${message}` : message);
  };

  const showWarning = (message, title = '') => {
    // Use error-style toast for warnings to keep them noticeable
    toast.error(title ? `${title}: ${message}` : message);
  };

  // Determine if we're in edit mode
  const isEditMode = !!editEmployeeId;

  // Get current user with permissions
  const getCurrentUserWithPermissions = () => {
    if (user && user.granularPermissions) {
      return user;
    }
    // Try to get from localStorage
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.granularPermissions) {
          return parsedUser;
        }
      }
    } catch (e) {
    }
    return user;
  };

  const currentUser = getCurrentUserWithPermissions();
  const currentUserRole = currentUser?.role;
  const isSuperAdmin = currentUserRole === USER_ROLES.SUPER_ADMIN || currentUserRole === 'super_admin';
  const currentUserPermissions = currentUser?.granularPermissions || {};

  // Check if current user can grant permissions based on organizational hierarchy
  const canGrantPermissions = () => {
    if (!currentUserRole) return false;
    // Check if current user has any assignable roles in the organizational hierarchy
    const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
    return assignableRoles.length > 0;
  };

  // Check if current user has a specific permission with a specific action
  const currentUserHasPermission = (permissionKey, action = 'read') => {
    // Super admin has all permissions
    if (currentUserRole === USER_ROLES.SUPER_ADMIN) {
      return true;
    }

    if (!currentUserPermissions || !currentUserPermissions[permissionKey]) {
      return false;
    }

    const permission = currentUserPermissions[permissionKey];
    return permission[action] === true;
  };

  // Filter permissions to only show what current user can grant
  const getFilteredPermissionCategories = () => {
    if (!canGrantPermissions()) {
      return {}; // Lowest level users cannot grant any permissions
    }

    const categories = {};
    Object.entries(PERMISSION_DEFINITIONS).forEach(([key, permission]) => {
      if (key !== 'all') {
        // Only include permissions that the current user has (at least read access)
        if (currentUserHasPermission(key, 'read')) {
          if (!categories[permission.category]) {
            categories[permission.category] = [];
          }
          categories[permission.category].push({ key, ...permission });
        }
      }
    });
    return categories;
  };

  // Filter role options to only show roles that current user can assign based on organizational hierarchy
  const getFilteredRoleOptions = () => {
    if (!canGrantPermissions()) {
      return {}; // Lowest level users cannot assign any roles
    }

    const filtered = {};

    // Get the list of roles that current user can assign from the organizational hierarchy
    const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];

    // Filter role options to only include roles that current user can assign
    Object.entries(roleOptions).forEach(([roleKey, roleData]) => {
      if (assignableRoles.includes(roleKey)) {
        filtered[roleKey] = roleData;
      }
    });

    return filtered;
  };

  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    personalEmail: '',
    mobile: '',
    personalPhoneNumber: '',
    dateOfBirth: '',
    gender: '',
    street: '',
    city: null,
    state: null,
    postalCode: '',
    country: null,

    // Permanent Address
    permanentStreet: '',
    permanentCity: null,
    permanentState: null,
    permanentPostalCode: '',
    permanentCountry: null,
    sameAsCurrentAddress: false,

    // Employment Information
    employeeId: '',
    department: '',
    designation: '',
    role: '',
    location: '',
    dateOfJoining: '',
    employmentType: '',
    team: '',
    manager: '',
    shift: '',
    salary: '',
    salaryStructureId: '',

    // Compliance/Payroll IDs
    pan: '',
    aadhaar: '',
    uan: '',
    esi: '',
    bankAccount: '',
    bankName: '',
    bankBranchName: '',
    ifsc: '',

    // Attachments
    resume: [],
    idProof: [],
    otherDocuments: [],

    // Profile Image
    profileImage: null,
    noticePeriod: ''
  });

  // UI state
  const [loading, setLoading] = useState(isEditMode); // Start with loading true if in edit mode
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // React Query Mutation for adding employee
  const addEmployeeMutation = useMutation({
    mutationFn: async (employeeData) => {
      return await hrService.addEmployee(employeeData);
    },
    onSuccess: (result) => {
      // Invalidate employee list queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });

  // React Query Mutation for updating employee
  const updateEmployeeMutation = useMutation({
    mutationFn: async ({ employeeId, updateData }) => {
      return await hrService.updateEmployee(employeeId, updateData);
    },
    onSuccess: (result) => {
      // Invalidate employee queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee', editEmployeeId] });
      if (formData.employeeId && formData.employeeId !== editEmployeeId) {
        queryClient.invalidateQueries({ queryKey: ['employee', formData.employeeId] });
      }
    },
  });

  // Role and Permission state
  const [selectedRole, setSelectedRole] = useState('');
  const [customPermissions, setCustomPermissions] = useState([]);
  const [showPermissionDetails, setShowPermissionDetails] = useState(false);
  const [granularPermissions, setGranularPermissions] = useState({});

  // Options for dropdowns - will be loaded from API
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [employeeTeams, setEmployeeTeams] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // State for searchable manager dropdown
  const [managerSearchOpen, setManagerSearchOpen] = useState(false);
  const [managerSearchTerm, setManagerSearchTerm] = useState('');
  const [debouncedManagerSearch, setDebouncedManagerSearch] = useState('');
  const [selectedManagerData, setSelectedManagerData] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialManagerName, setInitialManagerName] = useState('');

  // State for department dropdown
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');

  // State for searchable candidate dropdown
  const [candidateSearchOpen, setCandidateSearchOpen] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [debouncedCandidateSearch, setDebouncedCandidateSearch] = useState('');
  const [selectedCandidateData, setSelectedCandidateData] = useState(null);

  // State for add document form
  const [showAddDocumentForm, setShowAddDocumentForm] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [newDocumentTypeId, setNewDocumentTypeId] = useState('');
  const [newDocumentTypeCode, setNewDocumentTypeCode] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [newDocumentFile, setNewDocumentFile] = useState(null);
  const [newDocumentType, setNewDocumentType] = useState('resume');
  const [isUploadingInForm, setIsUploadingInForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [currentUploadedFile, setCurrentUploadedFile] = useState(null); // Confirmation modal state for document deletion
  // Confirmation modal state for document deletion
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    documentId: null,
    documentType: null,
    documentName: '',
    fileToDelete: null,
    arrayType: null,
    arrayIndex: null
  });

  // Profile image loading states
  const [isProfileImageUploading, setIsProfileImageUploading] = useState(false);
  const [isProfileImageDeleting, setIsProfileImageDeleting] = useState(false);

  // Personal Address location dropdown states (like JobApplication)
  const [personalCountryOptions, setPersonalCountryOptions] = useState([]);
  const [personalStateOptions, setPersonalStateOptions] = useState([]);
  const [personalCityOptions, setPersonalCityOptions] = useState([]);

  // Permanent Address location dropdown states
  const [permanentStateOptions, setPermanentStateOptions] = useState([]);
  const [permanentCityOptions, setPermanentCityOptions] = useState([]);

  // Load all countries on component mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c
    }));
    setPersonalCountryOptions(formattedCountries);

    // Fetch document types
    const fetchDocumentTypes = async () => {
      try {
        const response = await documentTypeService.getDropdown();
        if (response.success) {
          setDocumentTypes(response.data);
        }
      } catch (error) {
        showError('Failed to load document types. Some features may not work correctly.', 'Load Error');
      }
    };
    fetchDocumentTypes();
  }, []);

  // Sync personal state options when country changes
  useEffect(() => {
    if (formData.country && typeof formData.country === 'object' && formData.country.isoCode) {
      const states = State.getStatesOfCountry(formData.country.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setPersonalStateOptions(formattedStates);
    } else {
      setPersonalStateOptions([]);
    }
  }, [formData.country]);

  // Sync personal city options when state changes
  useEffect(() => {
    if (formData.country && typeof formData.country === 'object' && formData.state && typeof formData.state === 'object') {
      const cities = City.getCitiesOfState(formData.country.isoCode, formData.state.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setPersonalCityOptions(formattedCities);
    } else {
      setPersonalCityOptions([]);
    }
  }, [formData.country, formData.state]);

  // Sync permanent state options when country changes
  useEffect(() => {
    if (formData.permanentCountry && typeof formData.permanentCountry === 'object' && formData.permanentCountry.isoCode) {
      const states = State.getStatesOfCountry(formData.permanentCountry.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setPermanentStateOptions(formattedStates);
    } else {
      setPermanentStateOptions([]);
    }
  }, [formData.permanentCountry]);

  // Sync permanent city options when state changes
  useEffect(() => {
    if (formData.permanentCountry && typeof formData.permanentCountry === 'object' && formData.permanentState && typeof formData.permanentState === 'object') {
      const cities = City.getCitiesOfState(formData.permanentCountry.isoCode, formData.permanentState.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setPermanentCityOptions(formattedCities);
    } else {
      setPermanentCityOptions([]);
    }
  }, [formData.permanentCountry, formData.permanentState]);

  // Handle "Same as Current Address" changes
  useEffect(() => {
    if (formData.sameAsCurrentAddress) {
      setFormData(prev => ({
        ...prev,
        permanentStreet: prev.street,
        permanentCity: prev.city,
        permanentState: prev.state,
        permanentPostalCode: prev.postalCode,
        permanentCountry: prev.country
      }));
    }
  }, [
    formData.sameAsCurrentAddress,
    formData.street,
    formData.city,
    formData.state,
    formData.postalCode,
    formData.country
  ]);

  // Toggle Same as Current Address
  const handleSameAddressToggle = (e) => {
    const isChecked = e.target.checked;
    setFormData(prev => {
      if (isChecked) {
        return {
          ...prev,
          sameAsCurrentAddress: true,
          permanentStreet: prev.street,
          permanentCity: prev.city,
          permanentState: prev.state,
          permanentPostalCode: prev.postalCode,
          permanentCountry: prev.country
        };
      } else {
        return {
          ...prev,
          sameAsCurrentAddress: false
        };
      }
    });
  };

  // Upload progress state
  // const [uploadProgress, setUploadProgress] = useState({}); // { fileName: progressPercentage } // This was duplicated, removed the original one.

  const genders = ['male', 'female', 'other'];

  const formatEmploymentTypeLabel = (type) => {
    if (!type) return '';
    return String(type)
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '₹0';
    const parsed = Number(amount);
    if (Number.isNaN(parsed)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(parsed);
  };

  // Role mapping to match User model enum values
  const roleMapping = {
    'super_admin': 'Super Admin',
    'hr_manager': 'HR Manager',
    'hr_executive': 'HR Executive',
    'employee': 'Employee',
    // 'customer': 'Customer', // Commented out - customer creation disabled
    'sales_manager': 'Sales Manager',
    'sales_executive': 'Sales Executive',
    'dispatch_manager': 'Dispatch Manager',
    'support_manager': 'Support Manager'
  };

  const loadSalaryStructures = async () => {
    try {
      const baseUrl = API_BASE_URL;
      const response = await fetch(
        `${baseUrl}/payroll/salary-structures?status=active&limit=100`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        let structures = [];
        if (Array.isArray(result?.data?.structures)) {
          structures = result.data.structures;
        } else if (Array.isArray(result?.data)) {
          structures = result.data;
        } else if (Array.isArray(result?.structures)) {
          structures = result.structures;
        }
        const activeStructures = structures.filter(
          (structure) => (structure.status || '').toLowerCase() === 'active'
        );
        return { success: true, data: activeStructures };
      }
      return { success: false, data: [] };
    } catch (error) {
      return { success: false, data: [] };
    }
  };

  // Professional hierarchy roles with appropriate default permissions
  const roleOptions = {
    'super_admin': {
      label: 'Super Admin',
      permissions: [
        'dashboard', 'location_management', 'department_management',
        'recruitment', 'onboarding', 'employee_management', 'hr_backoffice',
        'attendance', 'payroll', 'performance', 'training', 'exit_management',
        'compliance_policy', 'reports',
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management',
        'analytics', 'target_achievement'
      ],
      description: 'Full system access with all CRUD operations across all modules'
    },
    'hr_manager': {
      label: 'HR Manager',
      permissions: [
        'dashboard', 'location_management', 'department_management', 'recruitment', 'onboarding',
        'employee_management', 'hr_backoffice', 'attendance', 'payroll',
        'performance', 'training', 'exit_management', 'compliance_policy', 'reports'
      ],
      description: 'Complete HRMS department management with full access to all HR functions'
    },
    'hr_executive': {
      label: 'HR Executive',
      permissions: [
        'recruitment', 'onboarding', 'employee_management',
        'attendance', 'training', 'performance'
      ],
      description: 'HR operations and support with operational-level access'
    },
    'employee': {
      label: 'Employee',
      permissions: [
        'self_service', 'my_attendance', 'my_payslip',
        'leave_request', 'my_training', 'my_performance', 'policies', 'submit_resignation'
      ],
      description: 'Employee self-service portal with access to own profile, attendance, payslips, leave, training, and performance'
    },
    'trainee': {
      label: 'Trainee',
      permissions: [
        'self_service', 'my_attendance', 'my_payslip',
        'leave_request', 'my_training', 'my_performance', 'policies', 'submit_resignation'
      ],
      description: 'Trainee self-service portal with access to own profile, attendance, payslips, leave, training, and performance'
    },
    'sales_manager': {
      label: 'Sales Manager',
      permissions: [
        'dashboard', 'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management',
        'analytics', 'target_achievement'
      ],
      description: 'Complete CRM and sales team management with full access to all sales functions'
    },
    'sales_executive': {
      label: 'Sales Executive',
      permissions: [
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management'
      ],
      description: 'Sales operations and customer engagement with standard operational access'
    },
    'dispatch_manager': {
      label: 'Dispatch Manager',
      permissions: [
        'order_management'
      ],
      description: 'Logistics and dispatch management with order tracking'
    },
    'support_manager': {
      label: 'Support Manager',
      permissions: [
        'customer_management'
      ],
      description: 'Customer support operations with access to customer relationships'
    },
  };

  // Available roles for dropdown (matching User model enum)
  const availableRoles = [
    { value: 'super_admin', label: 'Super Admin', department: 'IT' },
    { value: 'hr_manager', label: 'HR Manager', department: 'HR' },
    { value: 'hr_executive', label: 'HR Executive', department: 'HR' },
    { value: 'employee', label: 'Employee', department: 'General' },
    { value: 'trainee', label: 'Trainee', department: 'General' },
    // { value: 'customer', label: 'Customer', department: 'Support' }, // Commented out - customer creation disabled
    { value: 'sales_manager', label: 'Sales Manager', department: 'Sales' },
    { value: 'sales_executive', label: 'Sales Executive', department: 'Sales' },
    { value: 'dispatch_manager', label: 'Dispatch Manager', department: 'Logistics' },
    { value: 'support_manager', label: 'Support Manager', department: 'Support' }
  ];

  // Granular CRUD Permission definitions
  // This matches the exact sidebar structure shown in the application
  // Permission keys (like 'employee_management') must match backend middleware expectations
  // Labels match sidebar navigation labels exactly
  const PERMISSION_DEFINITIONS = {
    'all': {
      label: 'All Permissions',
      description: 'Full system access',
      category: 'System',
      crud: { read: true, create: true, update: true, delete: true }
    },

    // Dashboard (matching sidebar structure)
    'dashboard': {
      label: 'Dashboard',
      description: 'Access main dashboard and overview',
      category: 'Dashboard',
      crud: { read: true, create: false, update: false, delete: false }
    },

    // System Management (matching sidebar structure)
    'location_management': {
      label: 'Location Management',
      description: 'Manage locations and branches',
      category: 'System Management',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'department_management': {
      label: 'Department Management',
      description: 'Manage departments and organizational structure',
      category: 'System Management',
      crud: { read: true, create: true, update: true, delete: true }
    },

    // HRMS Module Permissions (matching sidebar structure)
    'recruitment': {
      label: 'Recruitment',
      description: 'Manage recruitment process and candidates',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'onboarding': {
      label: 'Onboarding',
      description: 'Manage onboarding process and joining kits',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'employee_management': {
      label: 'Employee Database',
      description: 'Manage employee records and database',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'hr_backoffice': {
      label: 'HR Dashboard',
      description: 'Manage HR back-office operations',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'attendance': {
      label: 'Attendance & Leave',
      description: 'Manage attendance records, logs and leave',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'payroll': {
      label: 'Payroll',
      description: 'Access payroll information and processing',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: false }
    },
    'performance': {
      label: 'Performance Management',
      description: 'Manage performance reviews, cycles and goals',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'training': {
      label: 'Training & Development',
      description: 'Manage training programs, learning paths and sessions',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'exit_management': {
      label: 'Exit Management',
      description: 'Manage employee exits and settlements',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'compliance_policy': {
      label: 'Compliance & Policy',
      description: 'Manage compliance and policy documents',
      category: 'HRMS',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'reports': {
      label: 'Analytics & Reporting',
      description: 'Access HRMS reports and analytics',
      category: 'HRMS',
      crud: { read: true, create: false, update: false, delete: false }
    },

    // CRM / Sales Module Permissions (matching sidebar structure)
    'lead_management': {
      label: 'Lead Management',
      description: 'Manage leads and lead pipeline',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'field_sales': {
      label: 'Field Sales',
      description: 'Manage field sales operations',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'opportunity_management': {
      label: 'Opportunity Management',
      description: 'Manage opportunities and sales pipeline',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'quotation_management': {
      label: 'Quotation & Proposal',
      description: 'Manage quotations and proposals',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'order_management': {
      label: 'Orders & Logistics',
      description: 'Manage orders and logistics',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'customer_management': {
      label: 'Customer Relationship',
      description: 'Manage customer relationships',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'analytics': {
      label: 'Analytics & Reporting',
      description: 'Access CRM reports and analytics',
      category: 'CRM / Sales',
      crud: { read: true, create: false, update: false, delete: false }
    },
    'target_achievement': {
      label: 'Target vs Achievement',
      description: 'Manage targets and view achievement reports',
      category: 'CRM / Sales',
      crud: { read: true, create: true, update: true, delete: true }
    },

    // Employee Self-Service Module Permissions (separate from HRMS/CRM)
    'self_service': {
      label: 'Self Service',
      description: 'Access own profile and employee self-service portal',
      category: 'Employee',
      crud: { read: true, create: false, update: true, delete: false }
    },
    'my_attendance': {
      label: 'My Attendance',
      description: 'View own attendance records and logs',
      category: 'Employee',
      crud: { read: true, create: false, update: false, delete: false }
    },
    'my_payslip': {
      label: 'My Payslips',
      description: 'View own payslips and salary information',
      category: 'Employee',
      crud: { read: true, create: false, update: false, delete: false }
    },
    'leave_request': {
      label: 'Apply Leave',
      description: 'Request and manage own leave applications',
      category: 'Employee',
      crud: { read: true, create: true, update: true, delete: true }
    },
    'my_training': {
      label: 'My Training',
      description: 'View assigned training programs and learning progress',
      category: 'Employee',
      crud: { read: true, create: false, update: false, delete: false }
    },
    'my_performance': {
      label: 'My Performance',
      description: 'View own performance reviews and goals',
      category: 'Employee',
      crud: { read: true, create: false, update: false, delete: false }
    },
    'policies': {
      label: 'Policies',
      description: 'View company policies and compliance documents',
      category: 'Employee',
      crud: { read: true, create: false, update: false, delete: false }
    },
    'submit_resignation': {
      label: 'Submit Resignation',
      description: 'Submit resignation and exit requests',
      category: 'Employee',
      crud: { read: true, create: true, update: true, delete: false }
    }
  };

  // Debounce manager search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedManagerSearch(managerSearchTerm);
    }, 300); // 300ms debounce delay for manager search

    return () => clearTimeout(timer);
  }, [managerSearchTerm]);

  // Debounce candidate search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCandidateSearch(candidateSearchTerm);
    }, 300); // 300ms debounce delay for candidate search

    return () => clearTimeout(timer);
  }, [candidateSearchTerm]);

  // React Query: Fetch Employees for Manager Dropdown (20 by default, then searchable)
  const { data: managerEmployeesData, isLoading: isLoadingManagerSearch, error: managerSearchError } = useQuery({
    queryKey: ['manager-employees-search', debouncedManagerSearch],
    queryFn: async () => {
      try {
        // If there's a search term, use search endpoint
        if (debouncedManagerSearch && debouncedManagerSearch.trim()) {
          const url = `${API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(debouncedManagerSearch.trim())}&includeSuperAdmin=true&activeOnly=true`;

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.responseCode === 200) {
              const employees = data.data?.employees || [];
              return employees;
            } else {
            }
          } else {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          }
          return [];
        } else {
          // Default: fetch 20 employees
          const url = `${API_BASE_URL}/dropdown-data/employees?limit=20&includeSuperAdmin=true&activeOnly=true`;

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.responseCode === 200) {
              const employees = data.data?.employees || [];
              return employees;
            } else {
            }
          } else {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          }
          return [];
        }
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    enabled: true, // Always enabled
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Get employees to display in manager dropdown
  const dropdownManagers = managerEmployeesData || [];

  // React Query: Fetch Hired Candidates for Dropdown (only name and email)
  const { data: candidateData, isLoading: isLoadingCandidateSearch } = useQuery({
    queryKey: ['hired-candidates-list', debouncedCandidateSearch],
    queryFn: async () => {
      // Build search query
      const searchParam = debouncedCandidateSearch && debouncedCandidateSearch.trim()
        ? `&search=${encodeURIComponent(debouncedCandidateSearch.trim())}`
        : '';

      // New API endpoint that returns only name, email, and _id for hired candidates
      const url = `${API_BASE_URL}/recruitment/candidates/hired-list?page=1&limit=20${searchParam}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          // New endpoint returns exactly what we need: id, firstName, lastName, name, email
          const candidates = data.data?.candidates || [];
          return candidates;
        }
      }
      return [];
    },
    staleTime: 0,
  });

  // Get candidates to display in dropdown
  const dropdownCandidates = candidateData || [];

  // State for loading full candidate details
  const [loadingCandidateDetails, setLoadingCandidateDetails] = useState(false);

  // Field-level validation state
  const [touched, setTouched] = useState({});

  // Close manager dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (managerSearchOpen && !event.target.closest('.manager-dropdown-container')) {
        setManagerSearchOpen(false);
        setManagerSearchTerm('');
      }
      if (departmentDropdownOpen && !event.target.closest('.department-dropdown-container')) {
        setDepartmentDropdownOpen(false);
        setDepartmentSearchTerm('');
      }
    };

    if (managerSearchOpen || departmentDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [managerSearchOpen, departmentDropdownOpen]);

  // Close candidate dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (candidateSearchOpen && !event.target.closest('.candidate-dropdown-container')) {
        setCandidateSearchOpen(false);
        setCandidateSearchTerm('');
      }
    };

    if (candidateSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [candidateSearchOpen]);

  // Load data from APIs
  useEffect(() => {
    loadDropdownData();
  }, []);

  // Load employee data when in edit mode
  useEffect(() => {
    if (isEditMode && editEmployeeId) {
      loadEmployeeData();
    }
  }, [isEditMode, editEmployeeId]);

  // Debug: Log formData documents when they change
  useEffect(() => {
    if (isEditMode && formData.resume && formData.idProof) {
    }
  }, [formData.resume, formData.idProof, isEditMode]);

  // Load employee data for editing
  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const result = await hrService.getEmployeeById(editEmployeeId);

      if (result.success && result.data) {
        const emp = result.data;

        // Prioritize user.role as it is the system source of truth
        // Fallback to mapping position if user.role is missing
        const roleValue = emp.user?.role || (
          Object.keys(roleMapping).find(
            key => roleMapping[key] === (emp.employmentInfo?.position || '')
          ) || (emp.employmentInfo?.position || '').toLowerCase().replace(/\s+/g, '_')
        );

        // Normalize department
        const departmentValue = emp.employmentInfo?.department || '';

        const extractSalaryStructureId = (salaryInfo, salaryStructure) => {
          if (!salaryInfo && !salaryStructure) return '';

          // 1. Check salaryInfo
          if (salaryInfo) {
            if (typeof salaryInfo === 'string') return salaryInfo;
            if (typeof salaryInfo === 'object') {
              if (salaryInfo._id) return salaryInfo._id.toString();
              if (salaryInfo.id) return salaryInfo.id.toString();
              if (salaryInfo.salaryStructureRef) {
                const ref = salaryInfo.salaryStructureRef;
                if (typeof ref === 'string') return ref;
                if (typeof ref === 'object' && (ref._id || ref.id)) return (ref._id || ref.id).toString();
              }
            }
          }

          // 2. Check salaryStructure fallback
          if (salaryStructure) {
            if (typeof salaryStructure === 'string') return salaryStructure;
            if (typeof salaryStructure === 'object') {
              if (salaryStructure._id) return salaryStructure._id.toString();
              if (salaryStructure.id) return salaryStructure.id.toString();
              if (salaryStructure.structureId) return salaryStructure.structureId.toString();
            }
          }

          return '';
        };

        const normalizedSalaryStructureId = extractSalaryStructureId(emp.salaryInfo, emp.salaryStructure);

        // Map employee data to form state
        const rawCountry = emp.contactInfo?.currentAddress?.country || 'India';
        const rawState = emp.contactInfo?.currentAddress?.state || '';
        const rawCity = emp.contactInfo?.currentAddress?.city || '';

        const getCountryObj = (raw) => {
          if (!raw) return null;
          if (typeof raw === 'object' && raw.isoCode) return raw;
          const name = typeof raw === 'object' ? raw.name : raw;
          return Country.getAllCountries().find(c => c.name === name || c.isoCode === name);
        };

        const getStateObj = (raw, country) => {
          if (!raw) return null;
          if (typeof raw === 'object' && raw.isoCode) return raw;
          const name = typeof raw === 'object' ? raw.name : raw;
          if (!country) return { name };
          return State.getStatesOfCountry(country.isoCode).find(s => s.name === name || s.isoCode === name) || { name };
        };

        const getCityObj = (raw, country, state) => {
          if (!raw) return null;
          if (typeof raw === 'object' && (raw.id || raw.name)) return raw;
          const name = typeof raw === 'object' ? raw.name : raw;
          if (!country || !state) return { name };
          return City.getCitiesOfState(country.isoCode, state.isoCode).find(c => c.name === name) || { name };
        };

        const countryObj = getCountryObj(rawCountry);
        const stateObj = getStateObj(rawState, countryObj);
        const cityObj = getCityObj(rawCity, countryObj, stateObj);

        // Normalize work location to a simple string label for dropdown
        let workLocationLabel = '';
        const workLocation = emp.employmentInfo?.workLocation;
        if (workLocation) {
          if (typeof workLocation === 'object') {
            workLocationLabel = workLocation.name || workLocation.label || workLocation.value || '';
          } else {
            workLocationLabel = workLocation;
          }
        }

        const employeeFormData = {
          firstName: emp.personalInfo?.firstName || '',
          lastName: emp.personalInfo?.lastName || '',
          email: emp.user?.email || '',
          personalEmail: emp.user?.personalEmail || '',
          mobile: emp.user?.phone || '',
          personalPhoneNumber: emp.user?.personalPhoneNumber || '',
          dateOfBirth: emp.personalInfo?.dateOfBirth
            ? new Date(emp.personalInfo.dateOfBirth).toISOString().split('T')[0]
            : '',
          gender: emp.personalInfo?.gender || '',
          employeeId: emp.employeeId || '',
          department: departmentValue,
          designation: emp.employmentInfo?.designation || '',
          role: roleValue,
          // Use plain string label so HTML <select> can auto-fill
          location: workLocationLabel,
          street: emp.contactInfo?.currentAddress?.street || '',
          city: cityObj,
          state: stateObj,
          postalCode: emp.contactInfo?.currentAddress?.pincode || '',
          country: countryObj,

          // Permanent Address
          permanentStreet: emp.contactInfo?.permanentAddress?.street || '',
          permanentCity: getCityObj(emp.contactInfo?.permanentAddress?.city, getCountryObj(emp.contactInfo?.permanentAddress?.country), getStateObj(emp.contactInfo?.permanentAddress?.state, getCountryObj(emp.contactInfo?.permanentAddress?.country))),
          permanentState: getStateObj(emp.contactInfo?.permanentAddress?.state, getCountryObj(emp.contactInfo?.permanentAddress?.country)),
          permanentPostalCode: emp.contactInfo?.permanentAddress?.pincode || '',
          permanentCountry: getCountryObj(emp.contactInfo?.permanentAddress?.country),
          sameAsCurrentAddress: false, // Default to false on load, can infer based on equality if needed but safer as false
          dateOfJoining: emp.employmentInfo?.joinDate
            ? new Date(emp.employmentInfo.joinDate).toISOString().split('T')[0]
            : '',
          employmentType: emp.employmentInfo?.employmentType || '',
          team: emp.employmentInfo?.team || '',
          noticePeriod: emp.employmentInfo?.noticePeriod || '',
          manager: emp.employmentInfo?.reportingManager?._id || emp.employmentInfo?.reportingManager?.id || '',
          shift: emp.employmentInfo?.shift?._id || emp.employmentInfo?.shift?.id || emp.employmentInfo?.shift || '',
          salary: emp.salaryInfo?.netPay || emp.salaryInfo?.ctc || emp.salaryInfo?.netSalary || emp.salaryInfo?.basicSalary || '',
          salaryStructureId: normalizedSalaryStructureId,
          pan: emp.complianceInfo?.pan || '',
          aadhaar: emp.complianceInfo?.aadhaar || '',
          uan: emp.complianceInfo?.uan || '',
          esi: emp.complianceInfo?.esi || '',
          bankAccount: emp.complianceInfo?.bankAccount || '',
          bankName: emp.complianceInfo?.bankName || '',
          bankBranchName: emp.complianceInfo?.bankBranchName || '',
          ifsc: emp.complianceInfo?.ifsc || '',
          // Map documents from API response
          resume: (() => {
            if (emp.documents?.resume && Array.isArray(emp.documents.resume) && emp.documents.resume.length > 0) {
              return emp.documents.resume.map(doc => ({
                fileUrl: doc.fileUrl,
                filename: doc.filename,
                originalName: doc.originalName,
                fileType: doc.fileType,
                fileSize: doc.fileSize,
                name: doc.name,
                documentName: doc.name || doc.documentName || 'Resume',
                documentType: 'resume',
                documentTypeId: doc.documentTypeId,
                documentTypeCode: doc.documentTypeCode,
                uploadedAt: doc.uploadedAt,
                _id: doc._id || doc.id
              }));
            }
            // Fallback: extract from otherDocuments if resume array is empty
            if (emp.documents?.otherDocuments && Array.isArray(emp.documents.otherDocuments)) {
              return emp.documents.otherDocuments
                .filter(doc => {
                  const name = (doc.name || '').toLowerCase();
                  return name.includes('resume') || name.includes('cv');
                })
                .map(doc => ({
                  fileUrl: doc.fileUrl,
                  filename: doc.filename,
                  originalName: doc.originalName,
                  fileType: doc.fileType,
                  fileSize: doc.fileSize,
                  name: doc.name,
                  documentName: doc.name || 'Resume',
                  documentType: 'resume',
                  documentTypeId: doc.documentTypeId,
                  documentTypeCode: doc.documentTypeCode,
                  uploadedAt: doc.uploadedAt,
                  _id: doc._id || doc.id
                }));
            }
            return [];
          })(),
          idProof: (() => {
            if (emp.documents?.idProof && Array.isArray(emp.documents.idProof) && emp.documents.idProof.length > 0) {
              return emp.documents.idProof.map(doc => ({
                fileUrl: doc.fileUrl,
                filename: doc.filename,
                originalName: doc.originalName,
                fileType: doc.fileType,
                fileSize: doc.fileSize,
                name: doc.name,
                documentName: doc.name || doc.documentName || 'ID Proof',
                documentType: 'idProof',
                documentTypeId: doc.documentTypeId,
                documentTypeCode: doc.documentTypeCode,
                uploadedAt: doc.uploadedAt,
                _id: doc._id || doc.id
              }));
            }
            return [];
          })(),
          otherDocuments: (() => {
            if (emp.documents?.otherDocuments && Array.isArray(emp.documents.otherDocuments)) {
              // Only include those that aren't categorized as resume above (for cleaner separation)
              return emp.documents.otherDocuments
                .filter(doc => {
                  const name = (doc.name || '').toLowerCase();
                  const isResume = name.includes('resume') || name.includes('cv');
                  return !isResume;
                })
                .map(doc => ({
                  fileUrl: doc.fileUrl,
                  filename: doc.filename,
                  originalName: doc.originalName,
                  fileType: doc.fileType,
                  fileSize: doc.fileSize,
                  name: doc.name,
                  documentName: doc.name || 'Document',
                  documentType: 'otherDocuments',
                  documentTypeId: doc.documentTypeId,
                  documentTypeCode: doc.documentTypeCode,
                  uploadedAt: doc.uploadedAt,
                  _id: doc._id || doc.id
                }));
            }
            return [];
          })()
        };

        setFormData(employeeFormData);

        // Set selected manager data if manager exists
        if (emp.employmentInfo?.reportingManager) {
          const mgr = emp.employmentInfo.reportingManager;
          setSelectedManagerData(mgr);
          const firstName = mgr.personalInfo?.firstName || mgr.firstName || '';
          const lastName = mgr.personalInfo?.lastName || mgr.lastName || '';
          const managerName = `${firstName} ${lastName}`.trim();
          setInitialManagerName(managerName);
        }

        // Set selected role
        if (roleValue) {
          setSelectedRole(roleValue);

          // If Super Admin, use handleRoleChange to set all permissions
          // Otherwise, load existing permissions or use role defaults
          if (roleValue === 'super_admin') {
            handleRoleChange(roleValue);
          } else {
            // Load user's granular permissions if available, otherwise use role defaults
            let savedPermissions = emp.user?.granularPermissions;
            if (savedPermissions instanceof Map || savedPermissions?.constructor?.name === 'Map') {
              savedPermissions = Object.fromEntries(savedPermissions);
            } else if (savedPermissions && typeof savedPermissions === 'object' && !Array.isArray(savedPermissions)) {
              savedPermissions = savedPermissions;
            } else {
              savedPermissions = null;
            }

            const hasSavedGranularPermissions = savedPermissions &&
              Object.keys(savedPermissions).length > 0 &&
              Object.values(savedPermissions).some(
                (perm) => perm && typeof perm === 'object' &&
                  (perm.read === true || perm.create === true || perm.update === true || perm.delete === true)
              );

            if (emp.user && hasSavedGranularPermissions) {
              // First, initialize ALL assignable permissions (set to false)
              // This ensures all permissions are in state, even if they weren't saved
              const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
              const allAssignablePermissions = new Set();
              assignableRoles.forEach(roleKey => {
                const rolePerms = roleOptions[roleKey]?.permissions || [];
                rolePerms.forEach(perm => {
                  if (perm !== 'all') {
                    // Only exclude dashboard if this role is HR Executive or Employee
                    if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
                      return; // Skip dashboard for HR Executive and Employee
                    }
                    allAssignablePermissions.add(perm);
                  }
                });
              });

              // Initialize all assignable permissions as false
              const initialGranularPermissions = {};
              allAssignablePermissions.forEach(permissionKey => {
                initialGranularPermissions[permissionKey] = {
                  read: false,
                  create: false,
                  update: false,
                  delete: false
                };
              });

              // Then overlay the saved permissions from the user
              Object.keys(savedPermissions).forEach(permKey => {
                const perm = savedPermissions[permKey];
                if (perm && typeof perm === 'object' && initialGranularPermissions.hasOwnProperty(permKey)) {
                  initialGranularPermissions[permKey] = {
                    read: perm.read === true,
                    create: perm.create === true,
                    update: perm.update === true,
                    delete: perm.delete === true
                  };
                }
              });

              setGranularPermissions(initialGranularPermissions);

              // Set custom permissions based on granular permissions
              const activePermissions = Object.keys(initialGranularPermissions).filter(
                key => initialGranularPermissions[key].read ||
                  initialGranularPermissions[key].create ||
                  initialGranularPermissions[key].update ||
                  initialGranularPermissions[key].delete
              );
              setCustomPermissions(activePermissions);
            } else {
              // No existing permissions, use role defaults
              handleRoleChange(roleValue);
            }
          }
        }

        // Load profile image if available
        if (emp.systemInfo?.profilePicture) {
          const cloudinaryInfo = extractCloudinaryInfo(emp.systemInfo.profilePicture);
          setFormData(prev => ({
            ...prev,
            profileImage: {
              fileUrl: emp.systemInfo.profilePicture,
              originalName: 'profile.jpg',
              fileType: 'image/jpeg',
              fileSize: 0,
              uploadedAt: new Date(),
              publicId: cloudinaryInfo?.publicId || null,
              resourceType: cloudinaryInfo?.resourceType || 'image'
            }
          }));
        }

        // Set initialFormData for tracking changes
        const initialDataObj = { ...employeeFormData };
        if (emp.systemInfo?.profilePicture) {
          const cloudinaryInfo = extractCloudinaryInfo(emp.systemInfo.profilePicture);
          initialDataObj.profileImage = {
            fileUrl: emp.systemInfo.profilePicture,
            originalName: 'profile.jpg',
            fileType: 'image/jpeg',
            fileSize: 0,
            uploadedAt: new Date(),
            publicId: cloudinaryInfo?.publicId || null,
            resourceType: cloudinaryInfo?.resourceType || 'image'
          };
        }
        setInitialFormData(initialDataObj);
      } else {
        showWarning(
          result.error || 'Unable to load employee data. Please try again.',
          'Unable to Load Employee Data'
        );
        navigate(-1);
      }
    } catch (error) {
      showWarning(
        'Unable to load employee data. Please check your connection and try again.',
        'Unable to Load Employee Data'
      );
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  // Initialize permissions list on component load based on assignable roles
  useEffect(() => {
    if (currentUserRole && Object.keys(granularPermissions).length === 0 && !isEditMode) {
      const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
      if (assignableRoles.length > 0) {
        // Initialize all assignable permissions as unselected
        const allAssignablePermissions = new Set();
        assignableRoles.forEach(roleKey => {
          const rolePerms = roleOptions[roleKey]?.permissions || [];
          rolePerms.forEach(perm => {
            if (perm !== 'all') {
              // Only exclude dashboard if this role is HR Executive or Employee
              if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
                return; // Skip dashboard for HR Executive and Employee
              }
              allAssignablePermissions.add(perm);
            }
          });
        });

        const initialGranularPermissions = {};
        allAssignablePermissions.forEach(permissionKey => {
          initialGranularPermissions[permissionKey] = {
            read: false,
            create: false,
            update: false,
            delete: false
          };
        });

        setGranularPermissions(initialGranularPermissions);
      }
    }
  }, [currentUserRole]);

  // Granular CRUD Permission definitions (duplicate - kept for reference, but PERMISSION_DEFINITIONS is used)
  // This matches PERMISSION_DEFINITIONS exactly
  const allPermissions = PERMISSION_DEFINITIONS;

  // Employee ID is now manually entered - no auto-generation

  const loadDropdownData = async () => {
    try {
      setLoading(true);

      // Load departments, locations, shifts, salary structures, employment types, and employee teams in parallel
      const [departmentsResult, locationsResult, shiftsResult, salaryStructuresResult, employmentTypesResult, employeeTeamsResult] = await Promise.all([
        hrService.getDepartments(),
        loadLocations(),
        loadShifts(),
        loadSalaryStructures(),
        employmentTypeService.getAll({ isActive: true }),
        employeeTeamService.getAll({ isActive: true })
      ]);

      if (departmentsResult.success) {
        setDepartments(departmentsResult.data);
      }

      // Use predefined roles instead of API
      setRoles(availableRoles);

      if (shiftsResult.success) {
        setShifts(shiftsResult.data || []);
      }

      if (salaryStructuresResult.success) {
        setSalaryStructures(salaryStructuresResult.data || []);
      }

      if (employmentTypesResult?.success) {
        const apiEmploymentTypes = Array.isArray(employmentTypesResult.data)
          ? employmentTypesResult.data
          : [];
        const options = apiEmploymentTypes
          .filter((item) => item?.name)
          .map((item) => item.name);

        setEmploymentTypes(options);
      } else {
        setEmploymentTypes(['full-time', 'part-time', 'contract', 'intern']);
      }

      if (employeeTeamsResult?.success) {
        const apiEmployeeTeams = Array.isArray(employeeTeamsResult.data)
          ? employeeTeamsResult.data
          : [];
        const options = apiEmployeeTeams
          .filter((item) => item?.name)
          .map((item) => item.name);

        setEmployeeTeams(options);
      } else {
        setEmployeeTeams([]);
      }
    } catch (error) {
      setEmploymentTypes(['full-time', 'part-time', 'contract', 'intern']);
      setEmployeeTeams([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch full candidate details by ID
  const fetchCandidateDetails = async (candidateId) => {
    try {
      setLoadingCandidateDetails(true);
      const url = `${API_BASE_URL}/recruitment/candidates/${candidateId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          // Backend returns { candidate: {...} } in data.data
          const candidate = data.data?.candidate || data.data || data.candidate;
          if (!candidate) {
            showWarning(
              'Candidate information not found. Please select a different candidate or continue without selecting one.',
              'Candidate Not Found'
            );
            return null;
          }
          return candidate;
        } else {
          showWarning(
            data.responseMessage || 'Unable to fetch candidate details. Please try again.',
            'Unable to Load Candidate'
          );
          return null;
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        showWarning(
          errorData.responseMessage || errorData.message || 'Unable to fetch candidate details. Please try again.',
          'Unable to Load Candidate'
        );
        return null;
      }
    } catch (error) {
      showWarning(
        'Unable to fetch candidate details. Please check your connection and try again.',
        'Unable to Load Candidate'
      );
      return null;
    } finally {
      setLoadingCandidateDetails(false);
    }
  };

  const loadShifts = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/shifts?isActive=true&limit=100`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Handle different response structures
        if (result.responseCode === 200 && result.data) {
          // Extract shifts from nested data structure (handle both .data and .shifts)
          const shiftsArray = result.data.shifts || result.data.data || result.data;
          if (Array.isArray(shiftsArray)) {
            return {
              success: true,
              data: shiftsArray
            };
          }
        } else if (result.success && result.data) {
          // Handle alternative response structure
          const shiftsArray = result.data.shifts || result.data.data || result.data;
          if (Array.isArray(shiftsArray)) {
            return {
              success: true,
              data: shiftsArray
            };
          }
        }
      }
      return { success: false, data: [] };
    } catch (error) {
      return { success: false, data: [] };
    }
  };

  // Load locations for dropdown
  const loadLocations = async () => {
    try {
      setLoadingLocations(true);
      const url = `${API_BASE_URL}/locations/active`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch locations: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const locationsData = (data.data || []).map((loc) => ({
          ...loc,
          // Ensure a consistent name field for existing usages
          name: loc.label || loc.name || loc.value
        }));

        setLocations(locationsData);
      } else {
        // Fallback to hardcoded locations
        setLocations(['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune']);
      }
    } catch (err) {
      // Fallback to hardcoded locations
      setLocations(['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune']);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Handle input changes with proper validation
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      handleFileUpload(name, files);
    } else {
      let processedValue = value;

      // Text fields: allow alphanumeric, spaces, and approved symbols
      const textFields = ['firstName', 'lastName', 'employeeId'];
      if (textFields.includes(name)) {
        processedValue = value.replace(INVALID_CHARS_REGEX, '');
        // Auto-capitalize first letter for names
        if (name === 'firstName' || name === 'lastName') {
          processedValue = processedValue.charAt(0).toUpperCase() + processedValue.slice(1);
        }
        // Enforce maxLength
        if (name === 'firstName' || name === 'lastName') {
          if (processedValue.length > 50) processedValue = processedValue.slice(0, 50);
        } else if (name === 'employeeId') {
          if (processedValue.length > 20) processedValue = processedValue.slice(0, 20);
        }
      }

      // Text fields with more lenient validation: allow approved symbols
      const generalTextFields = ['designation', 'bankName', 'bankBranchName', 'street', 'permanentStreet', 'city', 'state', 'country'];
      if (generalTextFields.includes(name)) {
        processedValue = value.replace(INVALID_CHARS_REGEX, '');
        // Enforce maxLength
        if (processedValue.length > 100) processedValue = processedValue.slice(0, 100);
      }

      // PAN: alphanumeric only, uppercase, max 10 chars
      if (name === 'pan') {
        processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 10);
      }

      // IFSC: alphanumeric only, uppercase, max 11 chars
      if (name === 'ifsc') {
        processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 11);
      }

      // Number fields: only allow digits, ensure positive (0 and greater)
      if (name === 'mobile') {
        processedValue = value.replace(/\D/g, '').slice(0, 10); // Exactly 10 digits
      } else if (['aadhaar', 'uan'].includes(name)) {
        processedValue = value.replace(/\D/g, '').slice(0, 12); // Exactly 12 digits
      } else if (name === 'esi') {
        processedValue = value.replace(/\D/g, '').slice(0, 10); // Exactly 10 digits
      } else if (name === 'bankAccount') {
        processedValue = value.replace(/\D/g, '').slice(0, 18); // Max 18 digits
        if (processedValue && parseInt(processedValue) < 0) processedValue = '0';
      } else if (name === 'salary') {
        // Only allow positive numbers (0 and greater), max 10 crore (100000000)
        processedValue = value.replace(/[^0-9]/g, '');
        if (processedValue && parseInt(processedValue) < 0) processedValue = '0';
        if (processedValue && parseInt(processedValue) > 100000000) processedValue = '100000000';
      } else if (name === 'postalCode' || name === 'permanentPostalCode') {
        processedValue = value.replace(/[^a-zA-Z0-9\s]/g, '').slice(0, 15); // Max 15 alphanumeric characters
      } else if (name === 'dateOfBirth') {
        // Validate date of birth - must be at least 18 years old
        if (value) {
          const today = new Date();
          const birthDate = new Date(value);
          const age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          const dayDiff = today.getDate() - birthDate.getDate();

          // Calculate actual age considering month and day
          const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

          if (actualAge < 18) {
            // Don't allow the date if age is less than 18
            setErrors(prev => ({
              ...prev,
              dateOfBirth: 'Employee must be at least 18 years old'
            }));
            return; // Don't update the form data
          } else {
            // Clear error if valid
            setErrors(prev => {
              const newErrors = { ...prev };
              if (newErrors.dateOfBirth === 'Employee must be at least 18 years old') {
                delete newErrors.dateOfBirth;
              }
              return newErrors;
            });
          }
        }
        processedValue = value;
      }

      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }


    // Clear ALL duplicate errors when user starts typing in any field
    // This prevents the cascade of error popups
    setErrors(prev => {
      const newErrors = { ...prev };
      // Clear the specific field error
      if (newErrors[name]) {
        newErrors[name] = '';
      }
      // Clear all duplicate errors for any field
      Object.keys(newErrors).forEach(key => {
        if (newErrors[key] && newErrors[key].includes('already exists')) {
          newErrors[key] = '';
        }
      });
      return newErrors;
    });
  };

  const handleSalaryStructureChange = (e) => {
    const selectedValue = e.target.value;
    const selectedStructure = salaryStructures.find((structure) => {
      const structureId = structure.id || structure._id || structure.structureId;
      return structureId === selectedValue;
    });

    setFormData((prev) => ({
      ...prev,
      salaryStructureId: selectedValue,
      salary: selectedStructure ? String(selectedStructure.netPay || selectedStructure.ctc || 0) : ''
    }));

    setErrors((prev) => {
      if (!prev.salary) return prev;
      const updatedErrors = { ...prev };
      delete updatedErrors.salary;
      return updatedErrors;
    });
  };

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  // Handle role selection
  const handleRoleChange = (role) => {
    // Check if current user can grant permissions
    if (!canGrantPermissions()) {
      showWarning(
        'You do not have permission to assign roles or grant permissions to others. Please contact an administrator.',
        'Permission Denied'
      );
      return;
    }

    setSelectedRole(role);

    // Find the role in availableRoles to get department
    const selectedRoleData = availableRoles.find(r => r.value === role);

    setFormData(prev => ({
      ...prev,
      role: role,
      // Only set department if it's currently empty; do not overwrite user's selection
      department: prev.department || (selectedRoleData ? selectedRoleData.department : '')
    }));

    // Clear role error
    if (errors.role) {
      setErrors(prev => ({ ...prev, role: null }));
    }

    // In edit mode, re-clicking the already selected role must not wipe loaded permissions
    if (isEditMode && role === selectedRole) {
      return;
    }

    // Set default permissions based on role
    // Pre-select the role's permissions, but keep all assignable permissions visible
    if (role && roleOptions[role]) {
      // Special handling for Super Admin: Select ALL permissions with ALL CRUD operations
      if (role === 'super_admin') {
        // Get ALL permissions from PERMISSION_DEFINITIONS (excluding 'all')
        const allPermissionKeys = Object.keys(PERMISSION_DEFINITIONS).filter(key => key !== 'all');

        // Set custom permissions to include all permission keys
        setCustomPermissions(allPermissionKeys);

        // Initialize granular permissions with ALL permissions set to ALL CRUD operations (read, create, update, delete) = true
        const initialGranularPermissions = {};
        allPermissionKeys.forEach(permissionKey => {
          initialGranularPermissions[permissionKey] = {
            read: true,
            create: true,
            update: true,
            delete: true
          };
        });

        setGranularPermissions(initialGranularPermissions);
        return; // Early return for Super Admin
      }

      // For other roles, use the existing logic
      let rolePermissions = roleOptions[role].permissions;

      // Filter out dashboard for HR Executive and Employee roles only
      if (role === 'hr_executive' || role === 'employee') {
        rolePermissions = rolePermissions.filter(perm => perm !== 'dashboard');
      }

      // Set custom permissions to the role's default permissions (without dashboard for HR Executive/Employee)
      setCustomPermissions(rolePermissions);

      // Initialize granular permissions based on role
      // Get all assignable permissions to initialize the full state
      const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
      const allAssignablePermissions = new Set();
      assignableRoles.forEach(roleKey => {
        const rolePerms = roleOptions[roleKey]?.permissions || [];
        rolePerms.forEach(perm => {
          if (perm !== 'all') {
            // Only exclude dashboard if this role is HR Executive or Employee
            if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
              return; // Skip dashboard for HR Executive and Employee
            }
            allAssignablePermissions.add(perm);
          }
        });
      });

      // Initialize all assignable permissions, but pre-select only the selected role's permissions
      const initialGranularPermissions = {};
      allAssignablePermissions.forEach(permissionKey => {
        const permissionDef = PERMISSION_DEFINITIONS[permissionKey];
        if (permissionDef) {
          // Skip dashboard for HR Executive and Employee roles
          if ((role === 'hr_executive' || role === 'employee') && permissionKey === 'dashboard') {
            initialGranularPermissions[permissionKey] = {
              read: false,
              create: false,
              update: false,
              delete: false
            };
          } else if (rolePermissions.includes(permissionKey)) {
            // If this permission is part of the selected role, use role defaults
            initialGranularPermissions[permissionKey] = {
              read: permissionDef.crud.read !== false,
              create: permissionDef.crud.create === true,
              update: permissionDef.crud.update === true,
              delete: permissionDef.crud.delete === true
            };
          } else {
            // Otherwise, set to false (not selected, but visible for custom assignment)
            initialGranularPermissions[permissionKey] = {
              read: false,
              create: false,
              update: false,
              delete: false
            };
          }
        }
      });

      setGranularPermissions(initialGranularPermissions);
    } else {
      // If no role selected, initialize all assignable permissions as unselected
      const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
      const allAssignablePermissions = new Set();
      assignableRoles.forEach(roleKey => {
        const rolePerms = roleOptions[roleKey]?.permissions || [];
        rolePerms.forEach(perm => {
          if (perm !== 'all') {
            // Only exclude dashboard if this role is HR Executive or Employee
            if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
              return; // Skip dashboard for HR Executive and Employee
            }
            allAssignablePermissions.add(perm);
          }
        });
      });

      const initialGranularPermissions = {};
      allAssignablePermissions.forEach(permissionKey => {
        initialGranularPermissions[permissionKey] = {
          read: false,
          create: false,
          update: false,
          delete: false
        };
      });

      setCustomPermissions([]);
      setGranularPermissions(initialGranularPermissions);
    }
  };

  // Get permission categories for organization
  // Show ALL permissions from ALL roles the current user can assign
  // The list doesn't change when role is selected - only the selections change
  // Exclude 'dashboard' permission only for HR Executive and Employee roles
  const getPermissionCategories = () => {
    if (!canGrantPermissions()) {
      return {}; // No permissions if user can't grant
    }

    // Get all roles the current user can assign
    const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];

    // Collect all unique permissions from all assignable roles
    const allAssignablePermissions = new Set();
    assignableRoles.forEach(roleKey => {
      const rolePerms = roleOptions[roleKey]?.permissions || [];
      rolePerms.forEach(perm => {
        if (perm !== 'all') {
          // Only exclude dashboard if this role is HR Executive or Employee
          if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
            return; // Skip dashboard for HR Executive and Employee
          }
          allAssignablePermissions.add(perm);
        }
      });
    });

    // Organize permissions by category
    const categories = {};
    allAssignablePermissions.forEach(permissionKey => {
      if (PERMISSION_DEFINITIONS[permissionKey]) {
        const permission = PERMISSION_DEFINITIONS[permissionKey];
        if (!categories[permission.category]) {
          categories[permission.category] = [];
        }
        categories[permission.category].push({ key: permissionKey, ...permission });
      }
    });

    return categories;
  };

  // Debounced duplicate check for real-time validation (disabled - backend handles comprehensive validation)
  const debouncedDuplicateCheck = useCallback(
    debounce(async (fieldName, value) => {
      // Skip real-time duplicate check - backend handles comprehensive validation
    }, 1500),
    []
  );

  // Check for duplicate entries
  const checkDuplicates = async () => {
    const duplicateErrors = {};

    try {
      // Get all employees first (without filters to check all fields)
      // Add timestamp to prevent caching
      const allEmployeesResponse = await hrService.getEmployees('limit=1000&_t=' + Date.now());

      if (allEmployeesResponse.success && allEmployeesResponse.data?.employees) {
        const allEmployees = allEmployeesResponse.data.employees;

        // Check if email already exists
        if (formData.email && formData.email.trim()) {
          const emailExists = allEmployees.some(emp =>
            emp.contactInfo?.personalEmail?.toLowerCase() === formData.email.toLowerCase() ||
            emp.user?.email?.toLowerCase() === formData.email.toLowerCase()
          );
          if (emailExists) {
            duplicateErrors.email = `Email "${formData.email}" already exists`;
          }
        }

        // Check if employee ID already exists
        if (formData.employeeId && formData.employeeId.trim()) {
          const employeeIdExists = allEmployees.some(emp =>
            emp.employeeId === formData.employeeId
          );
          if (employeeIdExists) {
            duplicateErrors.employeeId = `Employee ID "${formData.employeeId}" already exists`;
          }
        }

        // Check if PAN already exists (if provided)
        if (formData.pan && formData.pan.trim()) {
          const panExists = allEmployees.some(emp =>
            emp.complianceInfo?.pan === formData.pan
          );
          if (panExists) {
            duplicateErrors.pan = `PAN "${formData.pan}" already exists`;
          }
        }

        // Check if Aadhaar already exists (if provided)
        if (formData.aadhaar && formData.aadhaar.trim()) {
          const aadhaarExists = allEmployees.some(emp =>
            emp.complianceInfo?.aadhaar === formData.aadhaar
          );
          if (aadhaarExists) {
            duplicateErrors.aadhaar = `Aadhaar "${formData.aadhaar}" already exists`;
          }
        }
      }

    } catch (error) {
      // Don't block form submission if duplicate check fails
    }
    return duplicateErrors;
  };


  // Handle permission toggle
  const handlePermissionToggle = (permission) => {
    if (customPermissions.includes(permission)) {
      setCustomPermissions(prev => prev.filter(p => p !== permission));
    } else {
      setCustomPermissions(prev => [...prev, permission]);
    }
  };

  // Get current role permissions (excluding dashboard for HR Executive and below)
  const getCurrentRolePermissions = () => {
    if (selectedRole && roleOptions[selectedRole]) {
      // Filter out dashboard for HR Executive and Employee roles
      const rolePerms = roleOptions[selectedRole].permissions || [];
      if (selectedRole === 'hr_executive' || selectedRole === 'employee') {
        return rolePerms.filter(perm => perm !== 'dashboard');
      }
      return rolePerms;
    }
    return [];
  };

  // Handle granular CRUD permission toggle
  // Allow toggling any permission from assignable roles (for custom permissions)
  const handleGranularPermissionToggle = (permission, crudType) => {
    // Check if current user can grant permissions
    if (!canGrantPermissions()) {
      showWarning(
        'You do not have permission to grant permissions to others. Please contact an administrator.',
        'Permission Denied'
      );
      return;
    }

    // Check if this permission is from any assignable role
    const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
    const allAssignablePermissions = new Set();
    assignableRoles.forEach(roleKey => {
      const rolePerms = roleOptions[roleKey]?.permissions || [];
      rolePerms.forEach(perm => {
        if (perm !== 'all') {
          // Only exclude dashboard if this role is HR Executive or Employee
          if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
            return; // Skip dashboard for HR Executive and Employee
          }
          allAssignablePermissions.add(perm);
        }
      });
    });

    if (!allAssignablePermissions.has(permission)) {
      showWarning(
        'This permission is not available for the roles you can assign. Please select a different permission.',
        'Permission Not Available'
      );
      return;
    }

    setGranularPermissions(prev => {
      const updated = {
        ...prev,
        [permission]: {
          read: prev[permission]?.read || false,
          create: prev[permission]?.create || false,
          update: prev[permission]?.update || false,
          delete: prev[permission]?.delete || false,
          [crudType]: !prev[permission]?.[crudType]
        }
      };

      // Update customPermissions to include this permission if any CRUD is enabled
      const hasAnyPermission = updated[permission].read || updated[permission].create ||
        updated[permission].update || updated[permission].delete;

      setCustomPermissions(prevCustom => {
        if (hasAnyPermission && !prevCustom.includes(permission)) {
          return [...prevCustom, permission];
        } else if (!hasAnyPermission && prevCustom.includes(permission)) {
          return prevCustom.filter(p => p !== permission);
        }
        return prevCustom;
      });
      return updated;
    });
  };

  // Handle "Select All" for a specific module (all CRUD operations)
  // Selects ALL CRUD operations regardless of permission definition
  const handleModuleSelectAll = (permissionKey) => {
    // Check if current user can grant permissions
    if (!canGrantPermissions()) {
      showWarning(
        'You do not have permission to grant permissions to others. Please contact an administrator.',
        'Permission Denied'
      );
      return;
    }

    // Check if this permission is from any assignable role
    const assignableRoles = ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [];
    const allAssignablePermissions = new Set();
    assignableRoles.forEach(roleKey => {
      const rolePerms = roleOptions[roleKey]?.permissions || [];
      rolePerms.forEach(perm => {
        if (perm !== 'all') {
          // Only exclude dashboard if this role is HR Executive or Employee
          if (perm === 'dashboard' && (roleKey === 'hr_executive' || roleKey === 'employee')) {
            return; // Skip dashboard for HR Executive and Employee
          }
          allAssignablePermissions.add(perm);
        }
      });
    });

    if (!allAssignablePermissions.has(permissionKey)) {
      showWarning(
        'This permission is not available for the roles you can assign. Please select a different permission.',
        'Permission Not Available'
      );
      return;
    }

    const currentPerm = granularPermissions[permissionKey];
    // Check if all CRUD operations are selected
    const hasAllPermissions = currentPerm?.read === true &&
      currentPerm?.create === true &&
      currentPerm?.update === true &&
      currentPerm?.delete === true;

    setGranularPermissions(prev => {
      const updated = {
        ...prev,
        [permissionKey]: hasAllPermissions ? {
          // Deselect all - set all to false
          read: false,
          create: false,
          update: false,
          delete: false
        } : {
          // Select ALL CRUD operations regardless of permission definition
          read: true,
          create: true,
          update: true,
          delete: true
        }
      };

      // Update customPermissions
      const hasAnyPermission = updated[permissionKey].read || updated[permissionKey].create ||
        updated[permissionKey].update || updated[permissionKey].delete;

      setCustomPermissions(prevCustom => {
        if (hasAnyPermission && !prevCustom.includes(permissionKey)) {
          return [...prevCustom, permissionKey];
        } else if (!hasAnyPermission && prevCustom.includes(permissionKey)) {
          return prevCustom.filter(p => p !== permissionKey);
        }
        return prevCustom;
      });

      return updated;
    });
  };

  // Handle "Select All" for a category (all modules in category)
  // Selects ALL CRUD operations for all modules regardless of permission definitions
  const handleCategorySelectAll = (category) => {
    // Check if current user can grant permissions
    if (!canGrantPermissions()) {
      showWarning(
        'You do not have permission to grant permissions to others. Please contact an administrator.',
        'Permission Denied'
      );
      return;
    }

    const categories = getPermissionCategories();
    const categoryPermissions = categories[category] || [];

    // Check if all permissions in category are selected (all CRUD operations)
    const allSelected = isCategoryAllSelected(category);

    setGranularPermissions(prev => {
      const updated = { ...prev };
      const updatedCustomPermissions = new Set(customPermissions);

      categoryPermissions.forEach(({ key }) => {
        if (allSelected) {
          // Deselect all - set all to false
          updated[key] = {
            read: false,
            create: false,
            update: false,
            delete: false
          };
          updatedCustomPermissions.delete(key);
        } else {
          // Select ALL CRUD operations regardless of permission definition
          updated[key] = {
            read: true,
            create: true,
            update: true,
            delete: true
          };
          updatedCustomPermissions.add(key);
        }
      });

      setCustomPermissions(Array.from(updatedCustomPermissions));
      return updated;
    });
  };

  // Handle "Select All" for everything (all categories)
  // Selects ALL CRUD operations for all modules regardless of permission definitions
  const handleSelectAllCategories = () => {
    // Check if current user can grant permissions
    if (!canGrantPermissions()) {
      showWarning(
        'You do not have permission to grant permissions to others. Please contact an administrator.',
        'Permission Denied'
      );
      return;
    }

    const categories = getPermissionCategories();
    const allPermissions = Object.values(categories).flat();

    // Check if all permissions are selected (all CRUD operations)
    const allSelected = isAllCategoriesSelected();

    setGranularPermissions(prev => {
      const updated = { ...prev };
      const updatedCustomPermissions = new Set(customPermissions);

      allPermissions.forEach(({ key }) => {
        if (allSelected) {
          // Deselect all - set all to false
          updated[key] = {
            read: false,
            create: false,
            update: false,
            delete: false
          };
          updatedCustomPermissions.delete(key);
        } else {
          // Select ALL CRUD operations regardless of permission definition
          updated[key] = {
            read: true,
            create: true,
            update: true,
            delete: true
          };
          updatedCustomPermissions.add(key);
        }
      });

      setCustomPermissions(Array.from(updatedCustomPermissions));
      return updated;
    });
  };

  // Check if all permissions in a category are selected
  // Checks if ALL 4 CRUD operations are true for all modules (regardless of permission definition)
  const isCategoryAllSelected = (category) => {
    const categories = getPermissionCategories();
    const categoryPermissions = categories[category] || [];
    if (categoryPermissions.length === 0) return false;

    return categoryPermissions.every(({ key }) => {
      const perm = granularPermissions[key];
      if (!perm) return false;

      // Check if ALL CRUD operations are explicitly set to true
      return perm.read === true &&
        perm.create === true &&
        perm.update === true &&
        perm.delete === true;
    });
  };

  // Check if all permissions are selected
  // Checks if ALL 4 CRUD operations are true for all modules (regardless of permission definition)
  const isAllCategoriesSelected = () => {
    const categories = getPermissionCategories();
    const allPermissions = Object.values(categories).flat();
    if (allPermissions.length === 0) return false;

    return allPermissions.every(({ key }) => {
      const perm = granularPermissions[key];
      if (!perm) return false;

      // Check if ALL CRUD operations are explicitly set to true
      return perm.read === true &&
        perm.create === true &&
        perm.update === true &&
        perm.delete === true;
    });
  };

  // Check if all CRUD operations for a module are selected
  // Checks if ALL 4 CRUD operations are true (regardless of permission definition)
  const isModuleAllSelected = (permissionKey) => {
    const perm = granularPermissions[permissionKey];
    if (!perm) return false;

    // Check if ALL CRUD operations are explicitly set to true
    return perm.read === true &&
      perm.create === true &&
      perm.update === true &&
      perm.delete === true;
  };

  // Check if a permission is read-only (only shows Read checkbox)
  const isReadOnlyPermission = (permissionKey) => {
    const permissionDef = PERMISSION_DEFINITIONS[permissionKey];
    if (!permissionDef) return false;
    // Read-only if only read is true and others are false
    return permissionDef.crud.read === true &&
      permissionDef.crud.create === false &&
      permissionDef.crud.update === false &&
      permissionDef.crud.delete === false;
  };

  // Handle field blur for validation
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value, formData, isEditMode);

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Special handle blur for Select/Custom components
  const handleCustomBlur = (name, value) => {
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value, formData, isEditMode);

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Helper function to extract duplicate field from error message
  const extractDuplicateField = (errorMessage) => {
    if (errorMessage.includes('employeeId_1') || errorMessage.includes('employee ID')) {
      return { field: 'employeeId', name: 'Employee ID', value: formData.employeeId };
    } else if (errorMessage.includes('email_1') || errorMessage.includes('email')) {
      return { field: 'email', name: 'Email address', value: formData.email };
    } else if (errorMessage.includes('pan_1') || errorMessage.includes('complianceInfo.pan') || errorMessage.includes('PAN')) {
      return { field: 'pan', name: 'PAN number', value: formData.pan };
    } else if (errorMessage.includes('aadhaar_1') || errorMessage.includes('complianceInfo.aadhaar') || errorMessage.includes('Aadhaar')) {
      return { field: 'aadhaar', name: 'Aadhaar number', value: formData.aadhaar };
    } else if (errorMessage.includes('uan_1') || errorMessage.includes('complianceInfo.uan') || errorMessage.includes('UAN')) {
      return { field: 'uan', name: 'UAN (Universal Account Number)', value: formData.uan };
    } else if (errorMessage.includes('esi_1') || errorMessage.includes('complianceInfo.esi') || errorMessage.includes('ESI')) {
      return { field: 'esi', name: 'ESI number', value: formData.esi };
    } else if (errorMessage.includes('bankAccount_1') || errorMessage.includes('complianceInfo.bankAccount') || errorMessage.includes('Bank Account')) {
      return { field: 'bankAccount', name: 'Bank Account number', value: formData.bankAccount };
    }
    return { field: 'unknown', name: 'Unknown field', value: 'N/A' };
  };

  // Helper function to show warning alert for duplicate fields
  const showDuplicateWarning = (fieldInfo) => {
    const messages = {
      'employeeId': {
        title: 'Employee ID Already Exists',
        message: `The Employee ID "${fieldInfo.value}" is already registered in the system. Please use a different Employee ID.`
      },
      'email': {
        title: 'Email Already Exists',
        message: 'This email address is already registered in the system. Please use a different email address.'
      },
      'pan': {
        title: 'PAN Number Already Exists',
        message: `The PAN number "${fieldInfo.value}" is already registered in the system. Please use a different PAN number.`
      },
      'aadhaar': {
        title: 'Aadhaar Number Already Exists',
        message: `The Aadhaar number "${fieldInfo.value}" is already registered in the system. Please use a different Aadhaar number.`
      },
      'uan': {
        title: 'UAN Already Exists',
        message: `The UAN (Universal Account Number) "${fieldInfo.value}" is already registered in the system. Please use a different UAN.`
      },
      'esi': {
        title: 'ESI Number Already Exists',
        message: `The ESI number "${fieldInfo.value}" is already registered in the system. Please use a different ESI number.`
      },
      'bankAccount': {
        title: 'Bank Account Number Already Exists',
        message: `The Bank Account number "${fieldInfo.value}" is already registered in the system. Please use a different Bank Account number.`
      }
    };

    const alertInfo = messages[fieldInfo.field] || {
      title: `${fieldInfo.name} Already Exists`,
      message: `The ${fieldInfo.name.toLowerCase()} "${fieldInfo.value}" is already registered in the system. Please use a different ${fieldInfo.name.toLowerCase()}.`
    };

    showWarning(alertInfo.message, alertInfo.title);
  };

  const computeChanges = (initial, current) => {
    if (!initial || !current) return {};
    const changes = {};

    const getValueString = (val, fieldName) => {
      if (val === null || val === undefined || val === '') return 'Not Provided';

      if (typeof val === 'object' && val !== null) {
        if (val.name) return String(val.name).trim();
        if (val.label) return String(val.label).trim();
        return 'Provided';
      }

      if (fieldName === 'manager') {
        if (selectedManagerData && (selectedManagerData._id === val || selectedManagerData.id === val)) {
          const firstName = selectedManagerData.personalInfo?.firstName || selectedManagerData.firstName || '';
          const lastName = selectedManagerData.personalInfo?.lastName || selectedManagerData.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) return fullName;
        }
        if (initialManagerName && val === initialFormData?.manager) {
          return initialManagerName;
        }
        const strVal = String(val).trim();
        const normalized = strVal.toLowerCase().replace(/[\s-_]/g, '');
        if (normalized === 'notprovided') return 'Not Provided';
        return strVal;
      }

      if (fieldName === 'shift') {
        const found = shifts.find(s => (s._id || s.id) === val);
        if (found) return found.name || String(val);
      }

      if (fieldName === 'salaryStructureId') {
        const found = salaryStructures.find(s => (s._id || s.id) === val);
        if (found) return found.name || String(val);
      }

      const strVal = String(val).trim();
      const normalized = strVal.toLowerCase().replace(/[\s-_]/g, '');
      if (normalized === 'notprovided') return 'Not Provided';
      return strVal;
    };

    const fieldsToCompare = [
      { key: 'firstName', path: 'personalInfo.firstName' },
      { key: 'lastName', path: 'personalInfo.lastName' },
      { key: 'email', path: 'user.email' },
      { key: 'personalEmail', path: 'contactInfo.personalEmail' },
      { key: 'mobile', path: 'user.phone' },
      { key: 'personalPhoneNumber', path: 'contactInfo.personalPhone' },
      { key: 'dateOfBirth', path: 'personalInfo.dateOfBirth' },
      { key: 'gender', path: 'personalInfo.gender' },
      { key: 'street', path: 'contactInfo.currentAddress.street' },
      { key: 'city', path: 'contactInfo.currentAddress.city' },
      { key: 'state', path: 'contactInfo.currentAddress.state' },
      { key: 'postalCode', path: 'contactInfo.currentAddress.pincode' },
      { key: 'country', path: 'contactInfo.currentAddress.country' },
      { key: 'permanentStreet', path: 'contactInfo.permanentAddress.street' },
      { key: 'permanentCity', path: 'contactInfo.permanentAddress.city' },
      { key: 'permanentState', path: 'contactInfo.permanentAddress.state' },
      { key: 'permanentPostalCode', path: 'contactInfo.permanentAddress.pincode' },
      { key: 'permanentCountry', path: 'contactInfo.permanentAddress.country' },
      { key: 'employeeId', path: 'employeeId' },
      { key: 'department', path: 'employmentInfo.department' },
      { key: 'designation', path: 'employmentInfo.designation' },
      { key: 'role', path: 'user.role' },
      { key: 'location', path: 'employmentInfo.workLocation' },
      { key: 'dateOfJoining', path: 'employmentInfo.joinDate' },
      { key: 'employmentType', path: 'employmentInfo.employmentType' },
      { key: 'team', path: 'employmentInfo.team' },
      { key: 'manager', path: 'employmentInfo.reportingManager' },
      { key: 'shift', path: 'employmentInfo.shift' },
      { key: 'salaryStructureId', path: 'salaryStructure' },
      { key: 'pan', path: 'complianceInfo.pan' },
      { key: 'aadhaar', path: 'complianceInfo.aadhaar' },
      { key: 'uan', path: 'complianceInfo.uan' },
      { key: 'esi', path: 'complianceInfo.esi' },
      { key: 'bankAccount', path: 'complianceInfo.bankAccount' },
      { key: 'bankName', path: 'complianceInfo.bankName' },
      { key: 'bankBranchName', path: 'complianceInfo.bankBranchName' },
      { key: 'ifsc', path: 'complianceInfo.ifsc' },
      { key: 'noticePeriod', path: 'employmentInfo.noticePeriod' }
    ];

    fieldsToCompare.forEach(item => {
      const initialVal = getValueString(initial[item.key], item.key);
      const currentVal = getValueString(current[item.key], item.key);

      if (initialVal !== currentVal) {
        changes[item.path] = {
          old: initialVal,
          new: currentVal
        };
      }
    });

    return changes;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isProfileImageUploading) {
      showWarning('Please wait for the profile image to finish uploading.', 'Image Uploading');
      return;
    }

    if (isProfileImageDeleting) {
      showWarning('Please wait for the profile image to finish deleting.', 'Image Deleting');
      return;
    }

    // Validate all fields on submit
    const formErrors = validateEmployeeForm(formData, isEditMode);

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      // Scroll to first error
      const firstErrorKey = Object.keys(formErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      } else {
        showWarning('Please correct the errors in the form.', 'Validation Error');
      }
      return;
    }

    if (isEditMode) {
    } else {
    }

    try {
      // Get role label for position field
      const selectedRole = availableRoles.find(role => role.value === formData.role);
      const roleLabel = selectedRole ? selectedRole.label : formData.role;

      // Filter granularPermissions to only include modules with at least one CRUD operation set to true
      const filteredGranularPermissions = {};
      Object.keys(granularPermissions).forEach(module => {
        const perms = granularPermissions[module];
        if (perms && typeof perms === 'object') {
          // Only include if at least one CRUD operation is true
          if (perms.read === true || perms.create === true || perms.update === true || perms.delete === true) {
            filteredGranularPermissions[module] = perms;
          }
        }
      });

      // Filter permissions array to only include modules that have at least one CRUD operation set to true
      // This ensures we don't send hardcoded permissions that weren't explicitly granted
      const filteredPermissions = customPermissions.filter(permission => {
        // Include permission if it has at least one CRUD operation set to true in granularPermissions
        const perms = granularPermissions[permission];
        if (perms && typeof perms === 'object') {
          return perms.read === true || perms.create === true || perms.update === true || perms.delete === true;
        }
        // If no granular permission entry exists, exclude it
        return false;
      });

      if (isEditMode) {
        // Compute changes and updater metadata for profile update notifications
        const changes = computeChanges(initialFormData, formData);
        const updaterInfo = {
          name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'N/A',
          employeeId: user?.employeeId || user?.employee_id || 'N/A',
          email: user?.email || 'N/A',
          role: user?.role || 'N/A',
          department: user?.department || 'N/A'
        };

        // Prepare update data for API (edit mode)
        // Send data in the same format as creation for consistency
        const updateData = {
          changes,
          updaterInfo,
          // Top-level fields for User model update (same as creation)
          employeeId: formData.employeeId ? formData.employeeId.trim().toUpperCase() : undefined,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          personalEmail: formData.personalEmail,
          phone: formData.mobile,
          personalPhoneNumber: formData.personalPhoneNumber,
          role: formData.role, // Use the backend role value directly (enum value)
          department: formData.department,
          position: roleLabel, // Use the display label for position
          manager: formData.manager || null,
          // Role and Permission data - same format as creation
          permissions: filteredPermissions.length > 0 ? filteredPermissions : [],
          granularPermissions: Object.keys(filteredGranularPermissions).length > 0 ? filteredGranularPermissions : undefined,
          roleDescription: selectedRole ? roleOptions[selectedRole]?.description : '',
          // Nested structure for Employee model update
          personalInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            dateOfBirth: formData.dateOfBirth,
            gender: formData.gender
          },
          complianceInfo: {
            pan: formData.pan || '',
            aadhaar: formData.aadhaar || '',
            uan: formData.uan || '',
            esi: formData.esi || '',
            bankAccount: formData.bankAccount || '',
            bankName: formData.bankName || '',
            bankBranchName: formData.bankBranchName || '',
            ifsc: formData.ifsc || ''
          },
          contactInfo: {
            personalEmail: formData.personalEmail,
            personalPhone: formData.personalPhoneNumber,
            currentAddress: {
              street: formData.street || "Not provided",
              city: formData.city || { name: "Not provided" },
              state: formData.state || { name: "Not provided" },
              pincode: formData.postalCode || "000000",
              country: formData.country || { name: "India" }
            },
            permanentAddress: {
              street: formData.permanentStreet || "Not provided",
              city: formData.permanentCity || { name: "Not provided" },
              state: formData.permanentState || { name: "Not provided" },
              pincode: formData.permanentPostalCode || "000000",
              country: formData.permanentCountry || { name: "India" }
            }
          },
          employmentInfo: {
            department: formData.department,
            designation: formData.designation || '',
            position: roleLabel,
            workLocation: locations.find(l => l.name === formData.location) || { name: formData.location || 'Not provided' },
            joinDate: formData.dateOfJoining,
            employmentType: formData.employmentType,
            team: formData.team,
            reportingManager: formData.manager || null,
            shift: formData.shift,
            noticePeriod: formData.noticePeriod
          },
          salaryStructureId: formData.salaryStructureId || null,
          // Documents
          documents: {
            resume: formData.resume || [],
            idProof: formData.idProof || [],
            otherDocuments: formData.otherDocuments || []
          },
          // Profile Image
          profileImage: formData.profileImage || null
        };

        // Use React Query mutation to update employee
        const result = await updateEmployeeMutation.mutateAsync({
          employeeId: editEmployeeId,
          updateData
        });

        if (result.success) {
          showSuccess(`Employee updated successfully!`);

          // Navigate back to employee profile after a delay
          setTimeout(() => {
            navigate(-1);
          }, 2000);
        } else {
          // Handle errors
          const errorMessage = result.responseMessage || result.error || 'Unable to update employee. Please try again.';
          showError(errorMessage, 'Update Error');
        }
      } else {
        // Prepare data for API (add mode)
        const employeeData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          personalEmail: formData.personalEmail,
          phone: formData.mobile,
          personalPhoneNumber: formData.personalPhoneNumber,
          role: formData.role, // Use the backend role value directly (enum value)
          department: formData.department,
          position: roleLabel, // Use the display label for position
          employeeId: formData.employeeId,
          manager: formData.manager || null,
          joiningDate: formData.dateOfJoining,
          salary: parseFloat(formData.salary) || 0,
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.postalCode,
          country: formData.country,
          // Permanent Address (add mode)
          permanentStreet: formData.permanentStreet,
          permanentCity: formData.permanentCity,
          permanentState: formData.permanentState,
          permanentPincode: formData.permanentPostalCode,
          permanentCountry: formData.permanentCountry,
          location: locations.find(l => l.name === formData.location) || { name: formData.location || 'Not provided' },
          // Additional fields
          dateOfBirth: formData.dateOfBirth,
          gender: formData.gender,
          designation: formData.designation || '',
          employmentType: formData.employmentType,
          team: formData.team,
          shift: formData.shift,
          noticePeriod: formData.noticePeriod,
          pan: formData.pan || '',
          aadhaar: formData.aadhaar || '',
          uan: formData.uan || '',
          esi: formData.esi || '',
          bankAccount: formData.bankAccount || '',
          bankName: formData.bankName || '',
          bankBranchName: formData.bankBranchName || '',
          ifsc: formData.ifsc || '',
          // Documents
          resume: formData.resume || [],
          idProof: formData.idProof || [],
          otherDocuments: formData.otherDocuments || [],
          // Role and Permission data - only send permissions that have at least one CRUD operation set to true
          permissions: filteredPermissions.length > 0 ? filteredPermissions : [],
          granularPermissions: Object.keys(filteredGranularPermissions).length > 0 ? filteredGranularPermissions : undefined,
          roleDescription: selectedRole ? roleOptions[selectedRole]?.description : '',
          salaryStructureId: formData.salaryStructureId || null,
          // Profile Image
          profileImage: formData.profileImage || null,
          // Candidate Reference
          candidateId: selectedCandidateData?._id || selectedCandidateData?.id || null
        };

        // Use React Query mutation to add employee
        const result = await addEmployeeMutation.mutateAsync(employeeData);

        if (result.success) {
          // Show success message
          showSuccess(`Employee added successfully! Welcome email sent to ${formData.email}`);

          // Reset form
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            personalEmail: '',
            mobile: '',
            personalPhoneNumber: '',
            dateOfBirth: '',
            gender: '',
            employeeId: '',
            department: '',
            designation: '',
            role: '',
            location: '',
            dateOfJoining: '',
            employmentType: '',
            team: '',
            manager: '',
            shift: '',
            salary: '',
            salaryStructureId: '',
            pan: '',
            aadhaar: '',
            uan: '',
            esi: '',
            bankAccount: '',
            bankName: '',
            bankBranchName: '',
            ifsc: '',
            resume: [],
            idProof: [],
            profileImage: null,
            street: '',
            city: null,
            state: null,
            postalCode: '',
            country: null,
            permanentStreet: '',
            permanentCity: null,
            permanentState: null,
            permanentPostalCode: '',
            permanentCountry: null,
            sameAsCurrentAddress: false
          });

          // Reset manager search state
          setSelectedManagerData(null);
          setManagerSearchOpen(false);
          setManagerSearchTerm('');

          // Reset candidate search state
          setSelectedCandidateData(null);
          setCandidateSearchOpen(false);
          setCandidateSearchTerm('');

          // Navigate back to employee list after a delay
          if (onSuccess) {
            setTimeout(() => {
              onSuccess();
            }, 1000);
          } else {
            setTimeout(() => {
              navigate(-1);
            }, 2000);
          }
        } else {
          // Handle various error codes and duplicate cases
          const errorMessage = result.responseMessage || result.error || 'Unable to add employee. Please try again.';

          if (result.isDuplicate || result.responseCode === 409 || errorMessage.toLowerCase().includes('already exists')) {
            // Extract specific field from error message and set field error for better UX
            if (errorMessage.toLowerCase().includes('email')) {
              setErrors(prev => ({ ...prev, email: 'Email already exists' }));
            } else if (errorMessage.toLowerCase().includes('employee id') || errorMessage.toLowerCase().includes('employeeid')) {
              setErrors(prev => ({ ...prev, employeeId: 'Employee ID already exists' }));
            } else if (errorMessage.toLowerCase().includes('pan')) {
              setErrors(prev => ({ ...prev, pan: 'PAN already exists' }));
            } else if (errorMessage.toLowerCase().includes('aadhaar')) {
              setErrors(prev => ({ ...prev, aadhaar: 'Aadhaar already exists' }));
            }
            showWarning(errorMessage, 'Duplicate Entry');
          } else {
            showError(errorMessage, 'Submission Error');
          }
        }
      }
    } catch (error) {
      const backendMessage = error.response?.data?.responseMessage ||
        error.response?.data?.error ||
        error.message ||
        `Unable to ${isEditMode ? 'update' : 'add'} employee. Please try again.`;

      showError(backendMessage, isEditMode ? 'Update Failed' : 'Add Failed');

      // Still set some field errors if possible for common cases like 409
      if (error.response?.status === 409 || backendMessage.toLowerCase().includes('already exists')) {
        if (backendMessage.toLowerCase().includes('email')) {
          setErrors(prev => ({ ...prev, email: 'Email already exists' }));
        } else if (backendMessage.toLowerCase().includes('employee id') || backendMessage.toLowerCase().includes('employeeid')) {
          setErrors(prev => ({ ...prev, employeeId: 'Employee ID already exists' }));
        }
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (onClose) {
      onClose();
      return;
    }
    // Navigate back without confirmation dialog
    if (isEditMode && editEmployeeId) {
      navigate(-1);
    } else {
      navigate(-1);
    }
  };

  // Handle profile image upload
  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (only images)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showWarning('Please upload a valid image file (JPG, PNG, GIF, or WEBP).', 'Invalid File Type');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      showWarning('Image size must be less than 25MB. Please compress the image and try again.', 'File Too Large');
      e.target.value = ''; // Reset input
      return;
    }

    setIsProfileImageUploading(true);
    try {
      // If there's an existing profile image with publicId, delete it first
      if (formData.profileImage?.publicId) {
        await deleteFromCloudinary(formData.profileImage.publicId, formData.profileImage.resourceType);
      }

      // Instant upload to Cloudinary
      const tempId = `profile-${Date.now()}`;
      setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

      const result = await uploadToCloudinary(file, (progress) => {
        setUploadProgress(prev => ({ ...prev, [tempId]: progress }));
      });

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          profileImage: {
            fileUrl: result.data.url,
            fileType: file.type,
            fileSize: file.size,
            originalName: file.name,
            publicId: result.data.public_id,
            resourceType: result.data.resource_type,
            uploadedAt: new Date()
          }
        }));

        setTimeout(() => {
          setUploadProgress(prev => {
            const updated = { ...prev };
            delete updated[tempId];
            return updated;
          });
        }, 1000);
        showSuccess('Profile image updated successfully.');
      } else {
        showError(result.message, 'Image Upload Failed');
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[tempId];
          return updated;
        });
        e.target.value = '';
      }
    } catch (error) {
      showError('Failed to upload profile image. Please try again.');
    } finally {
      setIsProfileImageUploading(false);
    }
  };

  // Helper for Cloudinary deletion
  // Handle profile image delete
  const handleProfileImageDelete = async () => {
    if (!formData.profileImage?.publicId) {
      setFormData(prev => ({ ...prev, profileImage: null }));
      return;
    }

    setIsProfileImageDeleting(true);
    try {
      const result = await deleteFromCloudinary(formData.profileImage.publicId, formData.profileImage.resourceType);
      if (result) {
        setFormData(prev => ({ ...prev, profileImage: null }));
        showSuccess('Profile image removed successfully.');
      } else {
        showError('Failed to delete image from storage');
      }
    } catch (error) {
      showError('Failed to remove profile image. Please try again.');
    } finally {
      setIsProfileImageDeleting(false);
    }
  };

  const deleteFromCloudinary = async (publicId, resourceType = 'raw') => {
    try {
      const response = await del(`${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${publicId}&resource_type=${resourceType}`);
      return response.responseCode === 200;
    } catch (error) {
      return false;
    }
  };

  // Helper to extract Cloudinary publicId and resourceType from URL
  const extractCloudinaryInfo = (url) => {
    if (!url || !url.includes('cloudinary.com')) return null;

    try {
      const parts = url.split('/');
      const uploadIndex = parts.indexOf('upload');

      if (uploadIndex === -1) return null;

      // Resource type is usually just before /upload/
      const resourceType = parts[uploadIndex - 1] || 'image';

      // publicId starts after the version (v[digits])
      let publicIdWithExt = '';
      for (let i = uploadIndex + 1; i < parts.length; i++) {
        if (parts[i].startsWith('v') && /^\d+$/.test(parts[i].substring(1))) {
          publicIdWithExt = parts.slice(i + 1).join('/');
          break;
        }
      }

      // If no version found, use everything after /upload/
      if (!publicIdWithExt) {
        publicIdWithExt = parts.slice(uploadIndex + 1).join('/');
      }

      // Remove extension
      const lastDotIndex = publicIdWithExt.lastIndexOf('.');
      const publicId = lastDotIndex !== -1 ? publicIdWithExt.substring(0, lastDotIndex) : publicIdWithExt;

      return { publicId, resourceType };
    } catch (error) {
      return null;
    }
  };

  // Helper to upload file to Cloudinary
  const uploadToCloudinary = async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'employee-documents');

    try {
      const response = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      });

      if (response.responseCode === 200) {
        return { success: true, data: response.data };
      } else {
        return { success: false, message: response.responseMessage || 'Upload failed' };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Server error during upload'
      };
    }
  };

  // Handle file upload
  const handleFileUpload = async (name, files) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);

      // Validate each file
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 25 * 1024 * 1024; // 25MB

      for (const file of fileArray) {
        if (!allowedTypes.includes(file.type)) {
          showError(`File "${file.name}" is not a valid type. Please upload PDF, DOC, DOCX, JPG, or PNG files.`, 'Invalid File Type');
          continue;
        }

        if (file.size > maxSize) {
          showError(`File "${file.name}" is too large. Please upload files smaller than 25MB.`, 'File Too Large');
          continue;
        }

        // Instant upload to Cloudinary
        const tempId = `temp-${Date.now()}-${file.name}`;
        setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

        const result = await uploadToCloudinary(file, (progress) => {
          setUploadProgress(prev => ({ ...prev, [tempId]: progress }));
        });

        if (result.success) {
          const fileData = {
            filename: result.data.original_filename || file.name,
            originalName: result.data.original_filename || file.name,
            fileUrl: result.data.url,
            fileType: file.type,
            fileSize: file.size,
            publicId: result.data.public_id,
            resourceType: result.data.resource_type,
            uploadedAt: new Date()
          };

          setFormData(prev => ({
            ...prev,
            [name]: [...(prev[name] || []), fileData]
          }));

          // Clear progress after short delay
          setTimeout(() => {
            setUploadProgress(prev => {
              const updated = { ...prev };
              delete updated[tempId];
              return updated;
            });
          }, 1000);
        } else {
          showError(result.message, 'Upload Failed');
          setUploadProgress(prev => {
            const updated = { ...prev };
            delete updated[tempId];
            return updated;
          });
        }
      }
    }
  };

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (file, arrayType, arrayIndex) => {
    setDeleteConfirmModal({
      isOpen: true,
      fileToDelete: file,
      arrayType: arrayType,
      arrayIndex: arrayIndex
    });
  };

  // Handle file removal after confirmation
  const handleFileRemove = async (name, index) => {
    const fileToRemove = formData[name][index];

    // If it has a publicId, delete from Cloudinary
    if (fileToRemove?.publicId) {
      deleteFromCloudinary(fileToRemove.publicId, fileToRemove.resourceType || 'raw');
    }

    setFormData(prev => ({
      ...prev,
      [name]: prev[name].filter((_, i) => i !== index)
    }));
    // Close the modal
    setDeleteConfirmModal({
      isOpen: false,
      fileToDelete: null,
      arrayType: null,
      arrayIndex: null
    });
  };

  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'document').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || '').toLowerCase();
    if (type === 'application/pdf') return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type === 'image/jpeg') return `${clean}.jpg`;
    if (type === 'image/png') return `${clean}.png`;
    return clean;
  };

  const getFilenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const lastPart = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-zA-Z0-9]+$/.test(lastPart) ? lastPart : '';
    } catch (error) {
      return '';
    }
  };

  const extractObjectKeyFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      // Remove leading slash from pathname
      let key = urlObj.pathname.startsWith('/') ? urlObj.pathname.slice(1) : urlObj.pathname;
      // Remove bucket name if it's part of the path (virtual-hosted-style)
      if (key.startsWith('mobiloitte-intranet-bucket/')) {
        key = key.replace('mobiloitte-intranet-bucket/', '');
      }
      return key;
    } catch {
      return '';
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file download
  const handleFileDownload = async (file) => {
    if (file?.fileUrl) {
      const preferredName =
        file.originalName ||
        file.filename ||
        file.name ||
        getFilenameFromUrl(file.fileUrl) ||
        'document';

      const resolvedFilename = ensureFilenameHasExtension(preferredName, file.fileType);

      try {
        // Extract object key from URL
        const publicId = extractObjectKeyFromUrl(file.fileUrl);

        if (!publicId) {
          throw new Error('Unable to identify file');
        }

        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(resolvedFilename)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }

        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers.get('Content-Disposition');
        let finalFilename = resolvedFilename;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
          if (filenameMatch && filenameMatch[1]) {
            finalFilename = filenameMatch[1];
          }
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        triggerBrowserDownload(blobUrl, finalFilename);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (error) {
        toast.error(`Failed to download ${resolvedFilename}`);
      }
    } else if (file instanceof File) {
      // If it's a File object, create a download link
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = ensureFilenameHasExtension(file.name, file.type);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get file type icon/color
  const getFileTypeInfo = (file) => {
    const fileName = (file.originalName || file.name || '').toLowerCase();
    const fileUrl = (file.fileUrl || '').toLowerCase();
    const fileType = file.fileType || '';

    if (fileName.includes('.pdf') || fileUrl.includes('.pdf') || fileType.includes('pdf')) {
      return { type: 'PDF', color: 'text-red-600', bg: 'bg-red-50' };
    } else if (fileName.includes('.doc') || fileUrl.includes('.doc') || fileType.includes('word')) {
      return { type: 'DOC', color: 'text-brand', bg: 'bg-brand/5' };
    } else if (fileName.includes('.jpg') || fileName.includes('.jpeg') || fileUrl.includes('.jpg') || fileUrl.includes('.jpeg') || fileType.includes('jpeg')) {
      return { type: 'JPG', color: 'text-green-600', bg: 'bg-brand/5' };
    } else if (fileName.includes('.png') || fileUrl.includes('.png') || fileType.includes('png')) {
      return { type: 'PNG', color: 'text-purple-600', bg: 'bg-purple-50' };
    }
    return { type: 'FILE', color: 'text-gray-600', bg: 'bg-gray-50' };
  };

  // Handle file selection and instant upload in form
  const handleFileChangeInForm = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 25 * 1024 * 1024; // 25MB

    if (!allowedTypes.includes(file.type)) {
      showWarning('Invalid file type. Please upload PDF, DOC, DOCX, JPG, or PNG files only.', 'Invalid File Type');
      e.target.value = '';
      return;
    }

    if (file.size > maxSize) {
      showWarning(`The file "${file.name}" is too large. Please upload files smaller than 25MB.`, 'File Too Large');
      e.target.value = '';
      return;
    }

    setNewDocumentFile(file);
    setIsUploadingInForm(true);
    const tempId = 'form-upload';
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

    const result = await uploadToCloudinary(file, (progress) => {
      // Cap progress at 98% until server responds for a better user experience
      const displayProgress = Math.min(progress, 98);
      setUploadProgress(prev => ({ ...prev, [tempId]: displayProgress }));
    });

    if (result.success) {
      setUploadProgress(prev => ({ ...prev, [tempId]: 100 }));
      setCurrentUploadedFile({
        ...result.data,
        originalName: file.name,
        fileType: file.type,
        fileSize: file.size
      });
      showSuccess('File uploaded successfully', 'Upload Complete');
    } else {
      showError(result.message, 'Upload Failed');
      setNewDocumentFile(null);
    }

    setIsUploadingInForm(false);
    setUploadProgress(prev => {
      const updated = { ...prev };
      delete updated[tempId];
      return updated;
    });
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Create a mock event object to reuse handleFileChangeInForm
      const mockEvent = {
        target: {
          files: [file],
          value: '' // Dummy value
        }
      };
      await handleFileChangeInForm(mockEvent);
    }
  };

  // Handle adding new document
  const handleAddDocument = async () => {
    if (!newDocumentName.trim()) {
      showWarning('Please enter a document name.', 'Document Name Required');
      return;
    }

    if (!currentUploadedFile) {
      showWarning('Please wait for the file to finish uploading or select a file.', 'Upload Required');
      return;
    }

    const docNameLower = newDocumentName.trim().toLowerCase();
    const isResume = docNameLower.includes('resume') || docNameLower.includes('cv');
    const isIdProof = docNameLower.includes('aadhaar') || docNameLower.includes('pan') || docNameLower.includes('id proof') || docNameLower.includes('license') || docNameLower.includes('voter');
    const documentType = isResume ? 'resume' : (isIdProof ? 'idProof' : 'otherDocuments');

    const fileData = {
      name: newDocumentName.trim(), // Use 'name' for backend consistency
      filename: currentUploadedFile.original_filename || currentUploadedFile.originalName || newDocumentFile.name,
      originalName: currentUploadedFile.original_filename || currentUploadedFile.originalName || newDocumentFile.name,
      documentName: newDocumentName.trim(),
      fileUrl: currentUploadedFile.url,
      fileType: currentUploadedFile.fileType,
      fileSize: currentUploadedFile.fileSize,
      publicId: currentUploadedFile.public_id,
      resourceType: currentUploadedFile.resource_type,
      uploadedAt: new Date(),
      documentType: documentType,
      documentTypeId: newDocumentTypeId,
      documentTypeCode: newDocumentTypeCode // Add documentTypeCode to file data
    };

    setFormData(prev => ({
      ...prev,
      [documentType]: [...(prev[documentType] || []), fileData]
    }));

    // Reset form
    setNewDocumentName('');
    setNewDocumentTypeId('');
    setNewDocumentTypeCode('');
    setNewDocumentFile(null);
    setCurrentUploadedFile(null);
    setNewDocumentType('resume');
    setShowAddDocumentForm(false);
  };

  // Handle cancel add document
  const handleCancelAddDocument = () => {
    setNewDocumentName('');
    setNewDocumentTypeId('');
    setNewDocumentTypeCode('');
    setNewDocumentFile(null);
    setCurrentUploadedFile(null);
    setNewDocumentType('resume');
    setShowAddDocumentForm(false);
  };

  // Show loader when in edit mode and loading employee data
  if (isEditMode && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600">Loading employee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay for Candidate Details */}
      {loadingCandidateDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Candidate Details</h3>
              <p className="text-gray-600">Please wait while we fetch the candidate information...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={handleCancel}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
        </button>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Employee' : 'Add Employee'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update employee information' : 'Create a new employee record'}
          </p>
        </div>
        <div className="w-40"></div> {/* Spacer to balance the layout */}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8" noValidate>
        {/* Candidate Selection - At Top (Only show when adding new employee, not editing) */}
        {!isEditMode && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Candidate (Optional)
                {loadingCandidateDetails && (
                  <span className="ml-2 text-xs text-brand">
                    <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-brand mr-1"></span>
                    Loading candidate details...
                  </span>
                )}
              </label>
              <div className="relative candidate-dropdown-container w-full max-w-2xl">
                {/* Display selected candidate or placeholder */}
                <div
                  onClick={() => {
                    if (!loadingCandidateDetails) {
                      setCandidateSearchOpen(!candidateSearchOpen);
                      if (!candidateSearchOpen) {
                        setCandidateSearchTerm('');
                      }
                    }
                  }}
                  className={`mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white flex items-start justify-between gap-3 outline-none focus:outline-none focus-visible:outline-none ${loadingCandidateDetails
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-gray-400 focus-within:border-brand focus-within:ring-0'
                    }`}
                >
                  <span
                    className={`${selectedCandidateData ? 'text-gray-900' : 'text-gray-500'} flex-1 min-w-0 whitespace-normal break-words`}
                  >
                    {selectedCandidateData
                      ? `${selectedCandidateData.firstName || ""} ${selectedCandidateData.lastName || ""
                      }${selectedCandidateData.email ? ` (${selectedCandidateData.email})` : ""}`
                      : "Select candidate from hired list (optional)"}
                  </span>
                  <div className="flex-shrink-0 pt-1">
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${candidateSearchOpen ? "transform rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Dropdown with search */}
                {candidateSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by candidate name or email..."
                          value={candidateSearchTerm}
                          onChange={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setCandidateSearchTerm(value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Results list */}
                    <div className="max-h-64 overflow-y-auto">
                      {isLoadingCandidateSearch && debouncedCandidateSearch.trim() ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          Searching...
                        </div>
                      ) : dropdownCandidates.length > 0 ? (
                        dropdownCandidates.map((candidate) => (
                          <div
                            key={candidate._id || candidate.id}
                            onClick={async () => {
                              const candidateId = candidate._id || candidate.id;
                              if (!candidateId) {
                                showWarning(
                                  'Invalid candidate information. Please select a different candidate.',
                                  'Invalid Candidate'
                                );
                                return;
                              }

                              // Close dropdown immediately
                              setCandidateSearchOpen(false);
                              setCandidateSearchTerm("");

                              // Set basic candidate data for display
                              setSelectedCandidateData(candidate);

                              // Fetch full candidate details
                              const fullCandidate = await fetchCandidateDetails(candidateId);

                              if (!fullCandidate) {
                                showWarning(
                                  'Unable to fetch candidate details. Please try selecting the candidate again.',
                                  'Unable to Load Candidate Details'
                                );
                                return;
                              }

                              // Auto-fill form fields from full candidate data
                              // Extract location (city) from full location string if available
                              let locationValue = '';
                              if (fullCandidate.location) {
                                // Try to extract city from location string (format: "City, State, Country, Pincode")
                                const locationParts = fullCandidate.location.split(',');
                                locationValue = locationParts[0]?.trim() || fullCandidate.location;
                              }

                              // Format dateOfBirth if available
                              let dateOfBirthValue = '';
                              if (fullCandidate.dateOfBirth) {
                                const dob = new Date(fullCandidate.dateOfBirth);
                                if (!isNaN(dob.getTime())) {
                                  dateOfBirthValue = dob.toISOString().split('T')[0];
                                }
                              }

                              // Normalize gender to match form values (male, female, other)
                              let genderValue = '';
                              if (fullCandidate.gender) {
                                const genderLower = fullCandidate.gender.toLowerCase();
                                if (genderLower === 'male' || genderLower === 'm') {
                                  genderValue = 'male';
                                } else if (genderLower === 'female' || genderLower === 'f') {
                                  genderValue = 'female';
                                } else {
                                  genderValue = 'other';
                                }
                              }

                              // Convert resume to form format
                              const resumeFiles = [];
                              if (fullCandidate.resume) {
                                // Handle different resume structures
                                const resumeUrl = fullCandidate.resume.url || fullCandidate.resume.fileUrl || fullCandidate.resume.path;
                                const resumeFilename = fullCandidate.resume.filename || fullCandidate.resume.originalName || 'resume.pdf';

                                if (resumeUrl) {
                                  resumeFiles.push({
                                    filename: resumeFilename,
                                    originalName: fullCandidate.resume.originalName || resumeFilename,
                                    documentName: 'Resume', // Document name for display
                                    fileUrl: resumeUrl,
                                    fileType: fullCandidate.resume.mimetype ||
                                      (resumeUrl.includes('.pdf') ? 'application/pdf' :
                                        resumeUrl.includes('.doc') ? 'application/msword' :
                                          resumeUrl.includes('.docx') ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
                                            'application/octet-stream'),
                                    fileSize: fullCandidate.resume.size || fullCandidate.resume.fileSize || 0,
                                    uploadedAt: new Date(),
                                    documentType: 'resume'
                                  });
                                }
                              }

                              // Convert documents to idProof array
                              const idProofFiles = [];
                              if (fullCandidate.documents && Array.isArray(fullCandidate.documents)) {
                                fullCandidate.documents.forEach(doc => {
                                  if (doc.fileUrl && doc.originalName) {
                                    // Determine file type from URL or filename
                                    let fileType = 'application/pdf';
                                    const urlLower = doc.fileUrl.toLowerCase();
                                    const nameLower = doc.originalName.toLowerCase();
                                    if (urlLower.includes('.jpg') || urlLower.includes('.jpeg') || nameLower.includes('.jpg') || nameLower.includes('.jpeg')) {
                                      fileType = 'image/jpeg';
                                    } else if (urlLower.includes('.png') || nameLower.includes('.png')) {
                                      fileType = 'image/png';
                                    } else if (urlLower.includes('.doc') || nameLower.includes('.doc')) {
                                      fileType = 'application/msword';
                                    } else if (urlLower.includes('.docx') || nameLower.includes('.docx')) {
                                      fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                                    }

                                    // Format document type name for display
                                    const docTypeName = doc.documentType
                                      ? doc.documentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                                      : 'ID Proof';

                                    idProofFiles.push({
                                      filename: doc.filename || doc.originalName,
                                      originalName: doc.originalName,
                                      documentName: docTypeName, // Document name for display
                                      fileUrl: doc.fileUrl,
                                      fileType: fileType,
                                      fileSize: doc.fileSize || 0,
                                      uploadedAt: doc.uploadedAt ? new Date(doc.uploadedAt) : new Date(),
                                      documentType: doc.documentType || 'id_proof'
                                    });
                                  }
                                });
                              }

                              // Update form data with full candidate information
                              // Always replace with new candidate data when a candidate is selected
                              setFormData(prev => ({
                                ...prev,
                                // Personal Information - always update with candidate data
                                firstName: fullCandidate.firstName || '',
                                lastName: fullCandidate.lastName || '',
                                personalEmail: fullCandidate.email || '',
                                personalPhoneNumber: fullCandidate.phone || fullCandidate.mobile || '',
                                email: prev.email || '', // Retain if any, or leave empty for official email
                                mobile: prev.mobile || '', // Retain if any, or leave empty for official mobile
                                dateOfBirth: dateOfBirthValue || '',
                                gender: genderValue || '',
                                // Employment Information - always update with candidate data
                                department: fullCandidate.department || fullCandidate.jobDepartment || '',
                                location: locationValue || '',
                                salary: fullCandidate.expectedSalary || '',
                                noticePeriod: fullCandidate.noticePeriod || '',
                                // Attachments - always replace with candidate's documents
                                resume: resumeFiles,
                                idProof: idProofFiles,
                                // Keep these fields as they are (not from candidate data)
                                // employeeId, role, dateOfJoining, employmentType, manager, shift, salaryStructureId
                                // pan, aadhaar, uan, esi, bankAccount, ifsc
                              }));

                              // Update selected candidate data with full details
                              setSelectedCandidateData(fullCandidate);
                            }}
                            className={`px-4 py-2 hover:bg-brand/5 cursor-pointer border-b border-gray-100 ${selectedCandidateData?._id === candidate._id || selectedCandidateData?.id === candidate.id
                              ? "bg-brand/5"
                              : ""
                              }`}
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {candidate.firstName || ""}{" "}
                              {candidate.lastName || ""}
                            </div>
                            <div className="text-xs text-gray-500">
                              {candidate.email || ""}
                              {candidate.phone || candidate.mobile ? ` • ${candidate.phone || candidate.mobile}` : ""}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {debouncedCandidateSearch.trim()
                            ? "No hired candidates found"
                            : "Type to search hired candidates..."}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {candidateSearchOpen && debouncedCandidateSearch.trim()
                  ? `Found ${dropdownCandidates.length} candidate(s)`
                  : candidateSearchOpen
                    ? "Type to search hired candidates..."
                    : ""}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-brand" />
              Personal Information
            </h2>

            {/* Profile Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Image
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 relative">
                  {formData.profileImage?.fileUrl ? (
                    <img
                      src={formData.profileImage.fileUrl}
                      alt="Profile"
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                      <User className="h-10 w-10 text-gray-400" />
                    </div>
                  )}
                  {(isProfileImageUploading || isProfileImageDeleting) && (
                    <div className="absolute inset-0 bg-white bg-opacity-60 rounded-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleProfileImageUpload}
                    className="hidden"
                    disabled={isProfileImageUploading || isProfileImageDeleting}
                  />
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isProfileImageUploading || isProfileImageDeleting}
                      className={`mr-4 py-2 px-4 rounded-lg border-0 text-sm font-semibold transition-colors ${isProfileImageUploading || isProfileImageDeleting
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-brand text-white hover:bg-brand-dark cursor-pointer"
                        }`}
                    >
                      {isProfileImageUploading ? "Uploading..." : "Choose File"}
                    </button>
                    <span className="text-sm text-gray-500">
                      {formData.profileImage?.originalName || "No file chosen"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    JPG, PNG, GIF, or WEBP (Max 25MB)
                  </p>
                  {formData.profileImage && (
                    <button
                      type="button"
                      onClick={handleProfileImageDelete}
                      disabled={isProfileImageUploading || isProfileImageDeleting}
                      className={`mt-2 text-sm flex items-center transition-colors ${isProfileImageUploading || isProfileImageDeleting
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-red-600 hover:text-red-800"
                        }`}
                    >
                      {isProfileImageDeleting ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                          Removing...
                        </>
                      ) : (
                        <>
                          <X className="h-3 w-3 mr-1" />
                          Remove Image
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onKeyPress={handleTextKeyPress}
                  maxLength={50}
                  required
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.firstName && errors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="Enter first name"
                />
                <div className="flex justify-between items-center mt-1">
                  {touched.firstName && errors.firstName && (
                    <p className="text-sm text-red-600">{errors.firstName}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.firstName.length}/50 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onKeyPress={handleTextKeyPress}
                  maxLength={50}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.lastName && errors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="Enter last name"
                />
                <div className="flex justify-between items-center mt-1">
                  {touched.lastName && errors.lastName && (
                    <p className="text-sm text-red-600">{errors.lastName}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.lastName.length}/50 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Official Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    maxLength={100}
                    required
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.email && errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter email address"
                  />
                  {errors.email && errors.email.includes('already exists') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Duplicate detected"></div>
                    </div>
                  )}
                  {errors.email && errors.email.includes('already exists') && (
                    <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                      <div className="text-red-500 text-xs font-bold">DUPLICATE</div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center mt-1">
                  {touched.email && errors.email && (
                    <p className="text-sm text-red-600">{errors.email}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.email.length}/100 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Official Mobile
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    onKeyDown={handleNumberKeyDown}
                    maxLength={10}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.mobile && errors.mobile ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter mobile number"
                  />
                </div>
                <div className="flex justify-between items-center mt-1">
                  {touched.mobile && errors.mobile && (
                    <p className="text-sm text-red-600">{errors.mobile}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.mobile.length}/10 digits
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personal Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="email"
                    name="personalEmail"
                    value={formData.personalEmail || ''}
                    onChange={handleInputChange}
                    maxLength={100}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 border-gray-300`}
                    placeholder="Enter personal email address"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personal Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="tel"
                    name="personalPhoneNumber"
                    value={formData.personalPhoneNumber || ''}
                    onChange={handleInputChange}
                    onKeyDown={handleNumberKeyDown}
                    maxLength={15}
                    onBlur={handleBlur}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 border-gray-300`}
                    placeholder="Enter personal phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                    max={(() => {
                      const today = new Date();
                      const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]} // Cannot select dates older than 100 years
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.dateOfBirth && errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                </div>
                {touched.dateOfBirth && errors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.gender && errors.gender ? 'border-red-300' : 'border-gray-300'
                    }`}
                >
                  <option value="">Select gender</option>
                  {genders.map(gender => (
                    <option key={gender} value={gender}>{gender.charAt(0).toUpperCase() + gender.slice(1)}</option>
                  ))}
                </select>
                {touched.gender && errors.gender && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.gender}
                  </p>
                )}
              </div>

              {/* Address Details */}
              <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-brand" />
                  Current Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        placeholder="Street, building, area"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <LocationSelect
                      options={personalCountryOptions}
                      value={formData.country}
                      onChange={(country) => {
                        setFormData(prev => ({
                          ...prev,
                          country: country,
                          state: null,
                          city: null
                        }));
                        if (errors.country) setErrors(prev => ({ ...prev, country: null }));
                      }}
                      onBlur={() => handleCustomBlur('country', formData.country)}
                      placeholder="Select Country"
                    />
                    {touched.country && errors.country && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.country}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <LocationSelect
                      options={personalStateOptions}
                      value={formData.state}
                      disabled={!formData.country}
                      onChange={(state) => {
                        setFormData(prev => ({
                          ...prev,
                          state: state,
                          city: null
                        }));
                        if (errors.state || errors.city) setErrors(prev => ({ ...prev, state: null, city: null }));
                      }}
                      onBlur={() => handleCustomBlur('state', formData.state)}
                      placeholder="Select State"
                    />
                    {touched.state && errors.state && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.state}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <LocationSelect
                      options={personalCityOptions}
                      value={formData.city}
                      disabled={!formData.state}
                      onChange={(city) => {
                        setFormData(prev => ({
                          ...prev,
                          city: city
                        }));
                        if (errors.city || errors.state) setErrors(prev => ({ ...prev, city: null, state: null }));
                      }}
                      onBlur={() => handleCustomBlur('city', formData.city)}
                      placeholder="Select City"
                    />
                    {touched.city && errors.city && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.city}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Postal Code"
                      maxLength={15}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.postalCode && errors.postalCode ? 'border-red-300' : 'border-gray-300'
                        }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {touched.postalCode && errors.postalCode && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.postalCode}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {(formData.postalCode || '').length}/15 chars
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Permanent Address Details */}
              <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-brand" />
                    Permanent Address
                  </h3>
                  <label className="flex items-center space-x-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="sameAsCurrentAddress"
                      checked={formData.sameAsCurrentAddress}
                      onChange={handleSameAddressToggle}
                      className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                    />
                    <span className="text-sm text-gray-600">Same as Current Address</span>
                  </label>
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity duration-200 ${formData.sameAsCurrentAddress ? 'opacity-75 pointer-events-none' : ''}`}>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="permanentStreet"
                        value={formData.permanentStreet}
                        onChange={handleInputChange}
                        readOnly={formData.sameAsCurrentAddress}
                        placeholder="Street, building, area"
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formData.sameAsCurrentAddress ? 'bg-gray-50' : ''}`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <LocationSelect
                      options={personalCountryOptions}
                      value={formData.permanentCountry}
                      onChange={(country) => {
                        if (formData.sameAsCurrentAddress) return;
                        setFormData(prev => ({
                          ...prev,
                          permanentCountry: country,
                          permanentState: null,
                          permanentCity: null
                        }));
                        if (errors.permanentCountry) setErrors(prev => ({ ...prev, permanentCountry: null }));
                      }}
                      onBlur={() => handleCustomBlur('permanentCountry', formData.permanentCountry)}
                      placeholder="Select Country"
                      disabled={formData.sameAsCurrentAddress}
                    />
                    {touched.permanentCountry && errors.permanentCountry && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.permanentCountry}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <LocationSelect
                      options={permanentStateOptions}
                      value={formData.permanentState}
                      disabled={!formData.permanentCountry || formData.sameAsCurrentAddress}
                      onChange={(state) => {
                        if (formData.sameAsCurrentAddress) return;
                        setFormData(prev => ({
                          ...prev,
                          permanentState: state,
                          permanentCity: null
                        }));
                        if (errors.permanentState || errors.permanentCity) setErrors(prev => ({ ...prev, permanentState: null, permanentCity: null }));
                      }}
                      onBlur={() => {
                        handleCustomBlur('permanentState', formData.permanentState);
                        // Also clear city error if state became valid
                        if (formData.permanentState && errors.permanentCity) {
                          setErrors(prev => ({ ...prev, permanentCity: null }));
                        }
                      }}
                      placeholder="Select State"
                    />
                    {touched.permanentState && errors.permanentState && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.permanentState}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <LocationSelect
                      options={permanentCityOptions}
                      value={formData.permanentCity}
                      disabled={!formData.permanentState || formData.sameAsCurrentAddress}
                      onChange={(city) => {
                        if (formData.sameAsCurrentAddress) return;
                        setFormData(prev => ({
                          ...prev,
                          permanentCity: city
                        }));
                        if (errors.permanentCity || errors.permanentState) setErrors(prev => ({ ...prev, permanentCity: null, permanentState: null }));
                      }}
                      onBlur={() => {
                        handleCustomBlur('permanentCity', formData.permanentCity);
                        // Also clear state error if city is selected (rare case but consistent)
                        if (formData.permanentCity && errors.permanentState) {
                          setErrors(prev => ({ ...prev, permanentState: null }));
                        }
                      }}
                      placeholder="Select City"
                    />
                    {touched.permanentCity && errors.permanentCity && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.permanentCity}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="permanentPostalCode"
                      value={formData.permanentPostalCode}
                      onChange={handleInputChange}
                      readOnly={formData.sameAsCurrentAddress}
                      onBlur={handleBlur}
                      placeholder="Postal Code"
                      maxLength={15}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.permanentPostalCode && errors.permanentPostalCode ? 'border-red-300' : 'border-gray-300'
                        } ${formData.sameAsCurrentAddress ? 'bg-gray-50' : ''}`}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {touched.permanentPostalCode && errors.permanentPostalCode && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.permanentPostalCode}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {(formData.permanentPostalCode || '').length}/15 chars
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building className="h-5 w-5 mr-2 text-brand" />
            Employment Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee ID <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleInputChange}
                  onKeyPress={handleTextKeyPress}
                  maxLength={20}
                  required
                  disabled={isEditMode && !isSuperAdmin}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.employeeId && errors.employeeId ? 'border-red-300' : 'border-gray-300'
                    } ${isEditMode && !isSuperAdmin ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                  placeholder="Enter employee ID (e.g., EMP001, HR001)"
                />
                {touched.employeeId && errors.employeeId && errors.employeeId.includes('already exists') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Duplicate detected"></div>
                  </div>
                )}
                {touched.employeeId && errors.employeeId && errors.employeeId.includes('already exists') && (
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                    <div className="text-red-500 text-xs font-bold">DUPLICATE</div>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center mt-1">
                {touched.employeeId && errors.employeeId && (
                  <p className="text-sm text-red-600">{errors.employeeId}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.employeeId.length}/20 characters
                </p>
              </div>
            </div>

            <div className="relative department-dropdown-container">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
                {formData.role && (
                  <span className="text-xs text-gray-500 ml-2">(Auto-suggested based on role)</span>
                )}
              </label>

              <div
                onClick={() => setDepartmentDropdownOpen(!departmentDropdownOpen)}
                className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex justify-between items-center bg-white ${touched.department && errors.department
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300'
                  } outline-none focus:outline-none focus-visible:outline-none hover:border-gray-400`}
              >
                <span className={`block truncate ${formData.department ? 'text-gray-900' : 'text-gray-500'}`}>
                  {formData.department || "Select department"}
                </span>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>

              {departmentDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-hidden focus:outline-none sm:text-sm">
                  {/* Search Input */}
                  <div className="sticky top-0 z-10 bg-white px-3 py-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
                        placeholder="Search department..."
                        value={departmentSearchTerm}
                        onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  {/* Options List with Horizontal Scroll for long items */}
                  <ul className="max-h-48 overflow-auto">
                    {departments
                      .filter(dept => {
                        const deptName = (dept.name || dept).toString().toLowerCase();
                        return deptName.includes(departmentSearchTerm.toLowerCase());
                      })
                      .length > 0 ? (
                      departments
                        .filter(dept => {
                          const deptName = (dept.name || dept).toString().toLowerCase();
                          return deptName.includes(departmentSearchTerm.toLowerCase());
                        })
                        .map((dept) => {
                          const deptName = dept.name || dept;
                          const isSelected = formData.department === deptName;
                          return (
                            <li
                              key={dept.id || dept._id || dept}
                              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 min-w-full w-max ${isSelected ? 'bg-brand/5 text-brand' : 'text-gray-900'}`}
                              onClick={() => {
                                handleInputChange({ target: { name: 'department', value: deptName } });
                                setDepartmentDropdownOpen(false);
                                setDepartmentSearchTerm('');
                                // Trigger validation
                                handleCustomBlur('department', deptName);
                              }}
                            >
                              <span className={`block whitespace-nowrap ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                                {deptName}
                              </span>

                              {isSelected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-brand">
                                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </li>
                          );
                        })
                    ) : (
                      <li className="text-gray-500 cursor-default select-none py-2 pl-3 pr-9 text-center">
                        No departments found
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {touched.department && errors.department && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.department}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Designation
              </label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                maxLength={100}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.designation && errors.designation ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Enter designation (e.g., Software Engineer, HR Manager)"
              />
              <div className="flex justify-between items-center mt-1">
                {touched.designation && errors.designation && (
                  <p className="text-sm text-red-600">{errors.designation}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.designation.length}/100 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.location && errors.location ? 'border-red-300' : 'border-gray-300'
                    }`}
                >
                  <option value="">Select location</option>
                  {locations.map((loc) => {
                    const label = typeof loc === 'string'
                      ? loc
                      : (loc.label || loc.name || loc.value);
                    return (
                      <option
                        key={typeof loc === 'string' ? label : (loc.code || loc.value || label)}
                        value={label}
                      >
                        {label.charAt(0).toUpperCase() + label.slice(1)}
                      </option>
                    );
                  })}
                </select>
              </div>
              {touched.location && errors.location && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.location}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Joining <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="date"
                  name="dateOfJoining"
                  value={formData.dateOfJoining}
                  onChange={handleInputChange}
                  onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                  onBlur={handleBlur}
                  required
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.dateOfJoining && errors.dateOfJoining ? 'border-red-300' : 'border-gray-300'
                    }`}
                />
              </div>
              {touched.dateOfJoining && errors.dateOfJoining && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.dateOfJoining}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employment Type
              </label>
              <select
                name="employmentType"
                value={formData.employmentType}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.employmentType && errors.employmentType ? 'border-red-300' : 'border-gray-300'
                  }`}
              >
                <option value="">Select employment type</option>
                {Array.from(new Set([
                  ...employmentTypes,
                  ...(formData.employmentType ? [formData.employmentType] : [])
                ])).map((type) => (
                  <option key={type} value={type}>{formatEmploymentTypeLabel(type)}</option>
                ))}
              </select>
              {touched.employmentType && errors.employmentType && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.employmentType}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notice Period
              </label>
              <select
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.noticePeriod && errors.noticePeriod ? 'border-red-300' : 'border-gray-300'
                  }`}
              >
                <option value="">Select notice period</option>
                <option value="immediate">Immediate</option>
                <option value="15-days">15 days</option>
                <option value="30-days">30 days</option>
                <option value="45-days">45 days</option>
                <option value="60-days">60 days</option>
                <option value="90-days">90 days</option>
              </select>
              {touched.noticePeriod && errors.noticePeriod && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.noticePeriod}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team
              </label>
              <select
                name="team"
                value={formData.team}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand focus:outline-none focus:border-transparent ${touched.team && errors.team ? 'border-red-300' : 'border-gray-300'
                  }`}
              >
                <option value="">Select team</option>
                {Array.from(new Set([
                  ...employeeTeams,
                  ...(formData.team ? [formData.team] : [])
                ])).map((team) => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
              {touched.team && errors.team && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.team}
                </p>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manager
              </label>
              <div className="relative manager-dropdown-container">
                {/* Display selected manager or placeholder */}
                <div
                  onClick={() => {
                    const willOpen = !managerSearchOpen;
                    setManagerSearchOpen(willOpen);
                    if (willOpen) {
                      // Reset search term when opening to load default list
                      setManagerSearchTerm('');
                      // Trigger query by updating debounced search
                      setTimeout(() => {
                        setDebouncedManagerSearch('');
                      }, 100);
                    }
                  }}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white cursor-pointer flex items-start justify-between gap-3 hover:border-gray-400 focus-within:border-brand focus-within:ring-1 focus-within:ring-brand"
                >
                  <span
                    className={`${formData.manager ? 'text-gray-900' : 'text-gray-500'} flex-1 min-w-0 whitespace-normal break-words`}
                  >
                    {formData.manager && selectedManagerData
                      ? `${selectedManagerData.personalInfo?.firstName || ""} ${selectedManagerData.personalInfo?.lastName || ""
                      } (${selectedManagerData.employeeId || ""})`
                      : formData.manager
                        ? `Manager ID: ${formData.manager}`
                        : "Select manager (optional)"}
                  </span>
                  <div className="flex-shrink-0 pt-1">
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${managerSearchOpen ? "transform rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Dropdown with search */}
                {managerSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by employee name..."
                          value={managerSearchTerm}
                          onChange={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setManagerSearchTerm(value);
                          }}
                          onKeyPress={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Results list */}
                    <div className="max-h-64 overflow-y-auto">
                      {isLoadingManagerSearch && debouncedManagerSearch.trim() ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand mx-auto mb-2"></div>
                          Searching...
                        </div>
                      ) : managerSearchError ? (
                        <div className="p-4 text-center text-red-500 text-sm">
                          Error loading employees. Please try again.
                        </div>
                      ) : dropdownManagers.length > 0 ? (
                        dropdownManagers.map((manager) => {
                          const managerId = manager._id || manager.id;
                          const firstName = manager.personalInfo?.firstName || manager.firstName || '';
                          const lastName = manager.personalInfo?.lastName || manager.lastName || '';
                          const department = manager.employmentInfo?.department || manager.department || '';
                          const employeeId = manager.employeeId || '';
                          const email = manager.officialEmail || '';

                          // Format Role Text for display
                          let roleDisplay = manager.role || '';
                          if (roleDisplay) {
                            roleDisplay = roleDisplay.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          } else {
                            roleDisplay = 'Unknown Role';
                          }

                          return (
                            <div
                              key={managerId}
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  manager: managerId,
                                });
                                // Store the selected manager data for display
                                setSelectedManagerData(manager);
                                setManagerSearchOpen(false);
                                setManagerSearchTerm("");
                              }}
                              className={`px-4 py-3 hover:bg-brand/5 cursor-pointer border-b border-gray-100 ${formData.manager === managerId
                                ? "bg-brand/5"
                                : ""
                                }`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0 pr-4">
                                  <div className="text-sm font-semibold text-gray-900 truncate">
                                    {firstName} {lastName} {employeeId && <span className="text-gray-500 font-normal ml-1">({employeeId})</span>}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate mt-0.5">
                                    {email && <span>{email} • </span>}
                                    <span className="text-brand font-medium">{roleDisplay}</span>
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-200 text-right flex-shrink-0">
                                  {department || 'No Dept'}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {debouncedManagerSearch.trim()
                            ? "No employees found. Try a different search term."
                            : isLoadingManagerSearch
                              ? "Loading employees..."
                              : "Type to search employees or click to load default list"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {managerSearchOpen && debouncedManagerSearch.trim()
                  ? `Found ${dropdownManagers.length} employee(s)`
                  : managerSearchOpen
                    ? "Type to search employees..."
                    : ""}
              </div>
              {selectedManagerData && (
                <div className="mt-2 p-3 bg-brand/5 rounded-lg">
                  <div className="text-sm space-y-1">
                    <div className="font-medium text-brand break-words whitespace-normal">
                      {selectedManagerData.personalInfo?.firstName || ""}{" "}
                      {selectedManagerData.personalInfo?.lastName || ""}
                    </div>
                    <div className="text-brand break-words whitespace-normal">
                      {selectedManagerData.officialEmail || selectedManagerData.email || selectedManagerData.user?.email || "N/A"}
                    </div>
                    <div className="text-brand break-words whitespace-normal">
                      {selectedManagerData.employmentInfo?.department || "N/A"} •{" "}
                      {selectedManagerData.employmentInfo?.position || "N/A"}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shift
              </label>
              <select
                name="shift"
                value={formData.shift}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.shift && errors.shift ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              >
                <option value="">Select shift</option>
                {shifts
                  .filter((shift) => shift.isActive)
                  .map(shift => (
                    <option key={shift.id || shift._id} value={shift.id || shift._id}>
                      {shift.name} ({shift.startTime} - {shift.endTime})
                    </option>
                  ))}
              </select>
              {touched.shift && errors.shift && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.shift}
                </p>
              )}
              {shifts.filter((shift) => shift.isActive).length === 0 && (
                <div className="text-sm text-red-600 mt-1">
                  No active shifts available. Please create a shift first.
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salary Structure
              </label>
              <select
                name="salaryStructureId"
                value={formData.salaryStructureId}
                onChange={handleSalaryStructureChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${(touched.salaryStructureId && errors.salaryStructureId) || (touched.salary && errors.salary) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              >
                <option value="">Select salary structure</option>
                {salaryStructures.map((structure) => {
                  const structureId = structure.id || structure._id || structure.structureId;
                  const structureName = structure.name || 'Structure';
                  const structureCode = structure.code || '';
                  return (
                    <option key={structureId} value={structureId}>
                      {structureName}{structureCode ? ` · ${structureCode}` : ''}
                    </option>
                  );
                })}
              </select>
              {salaryStructures.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No active salary structures found. Please create one in Payroll to continue.
                </p>
              )}
              {formData.salaryStructureId && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected CTC:{' '}
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(
                      (salaryStructures.find((structure) => {
                        const structureId = structure.id || structure._id || structure.structureId;
                        return structureId === formData.salaryStructureId;
                      }) || {}).ctc || 0
                    )}
                  </span>
                </p>
              )}
              {((touched.salaryStructureId && errors.salaryStructureId) || (touched.salary && errors.salary)) && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.salaryStructureId || errors.salary}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Role and Permissions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2 text-brand" />
            Role & Permissions
          </h2>

          {/* Show message if user cannot grant permissions */}
          {!canGrantPermissions() && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                <p className="text-sm text-yellow-800">
                  <strong>Permission Restriction:</strong> You don’t have sufficient privileges to manage permissions. Please contact an administrator if you need access.
                </p>
              </div>
            </div>
          )}

          {/* Role Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Role <span className="text-red-500">*</span>
              {currentUserRole && (
                <span className="ml-2 text-xs text-gray-500">
                  (Logged in as: {roleMapping[currentUserRole] || currentUserRole})
                </span>
              )}
            </label>
            {canGrantPermissions() && currentUserRole && (
              <div className="mb-3 p-3 bg-brand/5 border border-brand/20 rounded-lg">
                <p className="text-xs text-brand">
                  <strong>Hierarchy:</strong> You can assign the following roles: {
                    (ROLE_ASSIGNMENT_HIERARCHY[currentUserRole] || [])
                      .map(role => roleMapping[role] || role)
                      .join(', ') || 'None'
                  }
                </p>
              </div>
            )}
            {Object.keys(getFilteredRoleOptions()).length === 0 ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  No roles available to assign. You can only assign roles below your level in the hierarchy.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.keys(getFilteredRoleOptions()).map(role => {
                  const roleData = getFilteredRoleOptions()[role];
                  return (
                    <div
                      key={role}
                      onClick={() => handleRoleChange(role)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedRole === role
                        ? 'border-brand bg-brand/5'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{roleData.label}</h3>
                        {selectedRole === role && (
                          <CheckCircle className="h-5 w-5 text-brand" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{roleData.description}</p>
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">
                          {roleData.permissions.filter(perm => {
                            // Exclude dashboard from count for HR Executive, Employee and Trainee
                            if ((role === 'hr_executive' || role === 'employee' || role === 'trainee') && perm === 'dashboard') {
                              return false;
                            }
                            return true;
                          }).length} permissions
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {touched.role && errors.role && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.role}
              </p>
            )}
          </div>

          {/* Permission Details */}
          {canGrantPermissions() && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-semibold text-gray-900">
                  {selectedRole ? `Permissions for ${roleOptions[selectedRole]?.label}` : 'Assign Permissions'}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPermissionDetails(!showPermissionDetails)}
                  className="text-sm text-brand hover:text-brand-dark"
                >
                  {showPermissionDetails ? 'Hide Details' : 'Show Details'}
                </button>
              </div>

              {/* Default Role Permissions - only show if role is selected */}
              {selectedRole && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Default Permissions:</h4>
                  <div className="flex flex-wrap gap-2">
                    {getCurrentRolePermissions().map(permission => (
                      <span
                        key={permission}
                        className="px-2 py-1 bg-brand/5 text-brand text-xs rounded-full"
                      >
                        {PERMISSION_DEFINITIONS[permission]?.label || permission}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Granular CRUD Permissions */}
              {showPermissionDetails && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-700">Granular CRUD Permissions:</h4>
                    {/* Top-level Select All */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="select-all-categories"
                        checked={isAllCategoriesSelected()}
                        onChange={handleSelectAllCategories}
                        className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                      />
                      <label htmlFor="select-all-categories" className="ml-2 text-sm font-medium text-gray-700">
                        Select All Categories
                      </label>
                    </div>
                  </div>
                  <div className="space-y-6">
                    {Object.entries(getPermissionCategories()).map(([category, permissions]) => (
                      <div key={category} className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-semibold text-gray-900 flex items-center">
                            <div className="w-2 h-2 bg-brand rounded-full mr-2"></div>
                            {category} Permissions
                          </h5>
                          {/* Category-level Select All */}
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={`select-all-${category}`}
                              checked={isCategoryAllSelected(category)}
                              onChange={() => handleCategorySelectAll(category)}
                              className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                            />
                            <label htmlFor={`select-all-${category}`} className="ml-2 text-xs font-medium text-gray-700">
                              Select All {category}
                            </label>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {permissions.map(({ key, label, description, crud: defaultCrud }) => {
                            const isReadOnly = isReadOnlyPermission(key);
                            return (
                              <div key={key} className="border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center flex-1">
                                    <div className="flex-1">
                                      <h6 className="text-sm font-medium text-gray-900">{label}</h6>
                                      <p className="text-xs text-gray-500">{description}</p>
                                    </div>
                                    {/* Module-level Select All - only show for non-read-only permissions */}
                                    {!isReadOnly && (
                                      <div className="flex items-center ml-4">
                                        <input
                                          type="checkbox"
                                          id={`select-all-${key}`}
                                          checked={isModuleAllSelected(key)}
                                          onChange={() => handleModuleSelectAll(key)}
                                          className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                                        />
                                        <label htmlFor={`select-all-${key}`} className="ml-2 text-xs font-medium text-brand">
                                          All
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-4 ml-4">
                                    {/* Read Permission - always show */}
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        id={`${key}-read`}
                                        checked={granularPermissions[key]?.read || false}
                                        onChange={() => handleGranularPermissionToggle(key, 'read')}
                                        className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                                      />
                                      <label htmlFor={`${key}-read`} className="ml-2 text-xs text-gray-700">
                                        Read
                                      </label>
                                    </div>

                                    {/* Create, Update, Delete - only show for non-read-only permissions */}
                                    {!isReadOnly && (
                                      <>
                                        {/* Create Permission */}
                                        <div className="flex items-center">
                                          <input
                                            type="checkbox"
                                            id={`${key}-create`}
                                            checked={granularPermissions[key]?.create || false}
                                            onChange={() => handleGranularPermissionToggle(key, 'create')}
                                            className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                                          />
                                          <label htmlFor={`${key}-create`} className="ml-2 text-xs text-gray-700">
                                            Create
                                          </label>
                                        </div>

                                        {/* Update Permission */}
                                        <div className="flex items-center">
                                          <input
                                            type="checkbox"
                                            id={`${key}-update`}
                                            checked={granularPermissions[key]?.update || false}
                                            onChange={() => handleGranularPermissionToggle(key, 'update')}
                                            className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                                          />
                                          <label htmlFor={`${key}-update`} className="ml-2 text-xs text-gray-700">
                                            Update
                                          </label>
                                        </div>

                                        {/* Delete Permission */}
                                        <div className="flex items-center">
                                          <input
                                            type="checkbox"
                                            id={`${key}-delete`}
                                            checked={granularPermissions[key]?.delete || false}
                                            onChange={() => handleGranularPermissionToggle(key, 'delete')}
                                            className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 focus:outline-none focus-visible:outline-none"
                                          />
                                          <label htmlFor={`${key}-delete`} className="ml-2 text-xs text-gray-700">
                                            Delete
                                          </label>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Permission Summary */}
                                <div className="flex items-center space-x-2 mt-2">
                                  <span className="text-xs text-gray-500">Current permissions:</span>
                                  {granularPermissions[key]?.read && (
                                    <span className="px-2 py-1 bg-brand/5 text-brand text-xs rounded-full">Read</span>
                                  )}
                                  {!isReadOnly && granularPermissions[key]?.create && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Create</span>
                                  )}
                                  {!isReadOnly && granularPermissions[key]?.update && (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Update</span>
                                  )}
                                  {!isReadOnly && granularPermissions[key]?.delete && (
                                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Delete</span>
                                  )}
                                  {!granularPermissions[key]?.read &&
                                    (!isReadOnly && !granularPermissions[key]?.create &&
                                      !granularPermissions[key]?.update && !granularPermissions[key]?.delete) && (
                                      <span className="text-xs text-gray-400">No permissions</span>
                                    )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Compliance/Payroll IDs */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CreditCard className="h-5 w-5 mr-2 text-brand" />
            Compliance & Payroll IDs
          </h2>
          <div className="mb-4 p-4 bg-brand/5 border border-brand/20 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-brand" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-brand">
                  Payroll Information
                </h3>
                <div className="mt-2 text-sm text-brand/80">
                  <p>Provide compliance and payroll details as available for accurate records.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PAN Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="pan"
                  value={formData.pan}
                  onChange={handleInputChange}
                  onKeyPress={(e) => {
                    // Allow alphanumeric only for PAN
                    if (!/^[a-zA-Z0-9]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  maxLength={10}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.pan && errors.pan ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="ABCDE1234F"
                />
                {touched.pan && errors.pan && errors.pan.includes('already exists') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Duplicate detected"></div>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center mt-1">
                {touched.pan && errors.pan && (
                  <p className="text-sm text-red-600">{errors.pan}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.pan.length}/10 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aadhaar Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="aadhaar"
                  value={formData.aadhaar}
                  onChange={handleInputChange}
                  onKeyDown={handleNumberKeyDown}
                  maxLength={12}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.aadhaar && errors.aadhaar ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="123456789012"
                />
                {touched.aadhaar && errors.aadhaar && errors.aadhaar.includes('already exists') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Duplicate detected"></div>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center mt-1">
                {touched.aadhaar && errors.aadhaar && (
                  <p className="text-sm text-red-600">{errors.aadhaar}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.aadhaar.length}/12 digits
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UAN (Universal Account Number)
              </label>
              <input
                type="text"
                name="uan"
                value={formData.uan}
                onChange={handleInputChange}
                onKeyDown={handleNumberKeyDown}
                maxLength={12}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.uan && errors.uan ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="123456789012"
              />
              <div className="flex justify-between items-center mt-1">
                {touched.uan && errors.uan && (
                  <p className="text-sm text-red-600">{errors.uan}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.uan.length}/12 digits
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ESI Number
              </label>
              <input
                type="text"
                name="esi"
                value={formData.esi}
                onChange={handleInputChange}
                onKeyDown={handleNumberKeyDown}
                maxLength={10}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.esi && errors.esi ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="1234567890"
              />
              <div className="flex justify-between items-center mt-1">
                {touched.esi && errors.esi && (
                  <p className="text-sm text-red-600">{errors.esi}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.esi.length}/10 digits
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bank Account Number
              </label>
              <input
                type="text"
                name="bankAccount"
                value={formData.bankAccount}
                onChange={handleInputChange}
                onKeyDown={handleNumberKeyDown}
                maxLength={18}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.bankAccount && errors.bankAccount ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="1234567890123456"
              />
              <div className="flex justify-between items-center mt-1">
                {touched.bankAccount && errors.bankAccount && (
                  <p className="text-sm text-red-600">{errors.bankAccount}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.bankAccount.length}/18 digits
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bank Name
              </label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                maxLength={100}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${errors.bankName ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Enter bank name (e.g., State Bank of India)"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.bankName && (
                  <p className="text-sm text-red-600">{errors.bankName}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.bankName.length}/100 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bank Branch Name
              </label>
              <input
                type="text"
                name="bankBranchName"
                value={formData.bankBranchName}
                onChange={handleInputChange}
                maxLength={100}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${errors.bankBranchName ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Enter bank branch name"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.bankBranchName && (
                  <p className="text-sm text-red-600">{errors.bankBranchName}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.bankBranchName.length}/100 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IFSC Code
              </label>
              <input
                type="text"
                name="ifsc"
                value={formData.ifsc}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  // Allow alphanumeric only for IFSC
                  if (!/^[a-zA-Z0-9]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={11}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400 ${touched.ifsc && errors.ifsc ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="ABCD0123456"
              />
              <div className="flex justify-between items-center mt-1">
                {touched.ifsc && errors.ifsc && (
                  <p className="text-sm text-red-600">{errors.ifsc}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.ifsc.length}/11 characters
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-brand" />
              Documents
            </h2>
            <button
              type="button"
              onClick={() => setShowAddDocumentForm(!showAddDocumentForm)}
              className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Document
            </button>
          </div>

          {/* Add Document Form */}
          {showAddDocumentForm && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Add New Document</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newDocumentTypeId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setNewDocumentTypeId(selectedId);
                      const selectedType = documentTypes.find(dt => dt._id === selectedId || dt.id === selectedId);
                      if (selectedType) {
                        setNewDocumentName(selectedType.name);
                        setNewDocumentTypeCode(selectedType.documentTypeCode || '');
                      } else {
                        setNewDocumentName('');
                        setNewDocumentTypeCode('');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg capitalize outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  >
                    <option value="">Select Document Type</option>
                    {documentTypes.map((type) => (
                      <option key={type._id || type.id} value={type._id || type.id} className="capitalize">
                        {type.name} {type.documentTypeCode ? `(${type.documentTypeCode})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Document <span className="text-red-500">*</span>
                  </label>
                  {currentUploadedFile ? (
                    <div className="flex items-center justify-between p-3 border border-brand bg-brand/5 rounded-lg">
                      <div className="flex items-center space-x-3 overflow-hidden">
                        <FileText className="h-5 w-5 text-brand flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate font-medium">
                          {currentUploadedFile.originalName || 'Uploaded Document'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {(currentUploadedFile.fileUrl || currentUploadedFile.url) && (
                          <button
                            type="button"
                            onClick={() => handleFileDownload({
                              fileUrl: currentUploadedFile.fileUrl || currentUploadedFile.url,
                              originalName: currentUploadedFile.originalName || currentUploadedFile.original_filename || newDocumentFile?.name,
                              filename: currentUploadedFile.filename,
                              fileType: currentUploadedFile.fileType || newDocumentFile?.type,
                              name: newDocumentName
                            })}
                            className="p-1 text-brand hover:bg-green-100 rounded transition-colors"
                            title="Download Document"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            deleteFromCloudinary(currentUploadedFile.public_id, currentUploadedFile.resource_type);
                            setCurrentUploadedFile(null);
                            setNewDocumentFile(null);
                          }}
                          className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Remove Document"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-all duration-200 ${isUploadingInForm ? 'border-brand bg-brand/5' :
                        isDragging ? 'border-brand bg-brand/5 border-2' : 'border-gray-300'
                        }`}>
                      <div className="flex flex-col items-center justify-center pt-5 pb-6 pointer-events-none">
                        {isUploadingInForm ? (
                          <>
                            <div className="w-10 h-10 mb-3 relative">
                              <svg className="w-full h-full -rotate-90">
                                <circle cx="20" cy="20" r="18" fill="none" stroke="#E2E8F0" strokeWidth="4" />
                                <circle
                                  cx="20"
                                  cy="20"
                                  r="18"
                                  fill="none"
                                  stroke="#0377e4"
                                  strokeWidth="4"
                                  strokeDasharray="113.1"
                                  strokeDashoffset={113.1 - (113.1 * (uploadProgress['form-upload'] || 0)) / 100}
                                  className="transition-all duration-300 ease-out"
                                />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-brand">
                                {uploadProgress['form-upload'] || 0}%
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 font-medium">
                              {(uploadProgress['form-upload'] || 0) >= 98 ? 'Processing...' : 'Uploading...'}
                            </p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mb-2 text-gray-400 group-hover:text-brand transition-colors" />
                            <p className="mb-1 text-sm text-gray-600">
                              <span className="font-semibold text-brand">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-400">PDF, DOC, JPG, PNG (Max 25MB)</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileChangeInForm}
                        disabled={isUploadingInForm}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleCancelAddDocument}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddDocument}
                  disabled={isUploadingInForm || !currentUploadedFile || !newDocumentName.trim()}
                  className={`px-5 py-2 rounded-lg transition-all text-sm font-semibold flex items-center ${isUploadingInForm || !currentUploadedFile || !newDocumentName.trim()
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-brand text-white hover:bg-brand-dark shadow-md hover:shadow-lg'
                    }`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Confirm & Add Document
                </button>
              </div>
            </div>
          )}

          {/* Documents Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Combine all documents (resume + idProof) for display */}
                  {(() => {
                    const allDocuments = [];

                    // Add resume documents with metadata
                    if (formData.resume && formData.resume.length > 0) {
                      formData.resume.forEach((file, index) => {
                        allDocuments.push({
                          ...file,
                          arrayType: 'resume',
                          arrayIndex: index
                        });
                      });
                    }

                    // Add idProof documents with metadata
                    if (formData.idProof && formData.idProof.length > 0) {
                      formData.idProof.forEach((file, index) => {
                        allDocuments.push({
                          ...file,
                          arrayType: 'idProof',
                          arrayIndex: index
                        });
                      });
                    }

                    // Add otherDocuments with metadata
                    if (formData.otherDocuments && formData.otherDocuments.length > 0) {
                      formData.otherDocuments.forEach((file, index) => {
                        allDocuments.push({
                          ...file,
                          arrayType: 'otherDocuments',
                          arrayIndex: index
                        });
                      });
                    }

                    if (allDocuments.length === 0) {
                      return (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                            <div className="flex flex-col items-center">
                              <FileText className="h-12 w-12 text-gray-300 mb-2" />
                              <p>No documents added yet</p>
                              <p className="text-xs mt-1">Use the button above to add documents</p>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return [...allDocuments, ...Object.entries(uploadProgress).map(([tempId, progress]) => ({
                      tempId,
                      progress,
                      documentName: tempId.split('-').pop(),
                      filename: tempId.split('-').pop(),
                      isUploading: true
                    }))].map((file, index) => {
                      if (file.isUploading) {
                        return (
                          <tr key={file.tempId} className="bg-brand/5 animate-pulse">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 bg-brand/5 rounded flex items-center justify-center mr-3">
                                  <div className="h-4 w-4 border-2 border-brand border-t-transparent rounded-full animate-spin"></div>
                                </div>
                                <span className="text-sm font-medium text-gray-900">Uploading...</span>
                              </div>
                            </td>
                            <td className="px-6 py-4" colSpan="2">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-brand h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${file.progress}%` }}
                                ></div>
                              </div>
                              <div className="flex justify-between mt-1">
                                <span className="text-[10px] text-gray-500">{file.filename}</span>
                                <span className="text-[10px] font-semibold text-brand">{file.progress}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button disabled className="text-gray-400 cursor-not-allowed">
                                <X className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      }

                      const fileInfo = getFileTypeInfo(file);
                      const documentName = file.name || file.documentName || file.documentType || (file.arrayType === 'resume' ? 'Resume' : 'ID Proof');
                      const documentCode = file.documentTypeCode ? `(${file.documentTypeCode})` : '';
                      const fileName = file.filename || file.originalName || file.name || 'Untitled';

                      return (
                        <tr key={`${file.arrayType}-${file.arrayIndex}`} className="hover:bg-gray-50 transition-colors group">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-8 w-8 ${fileInfo.bg} rounded-lg flex items-center justify-center mr-3 shadow-sm group-hover:scale-110 transition-transform`}>
                                <FileText className={`h-4 w-4 ${fileInfo.color}`} />
                              </div>
                              <span className="text-sm font-medium text-gray-900">{documentName} <span className="text-xs text-gray-400 font-normal">{documentCode}</span></span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 truncate max-w-xs font-medium" title={fileName}>
                              {fileName}
                            </div>
                            <div className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mt-0.5">{fileInfo.type}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatFileSize(file.fileSize || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-3">
                              {file.fileUrl && (
                                <button
                                  type="button"
                                  onClick={() => handleFileDownload(file)}
                                  className="text-gray-400 hover:text-brand transition-all p-1.5 rounded-lg hover:bg-brand/5"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              )}
                              <PermissionGate permission="employee_management" action="update">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(file, file.arrayType, file.arrayIndex)}
                                  className="text-gray-400 hover:text-red-600 transition-all p-1.5 rounded-lg hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {/* Info Message */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                <strong>Note:</strong> Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 25MB per file)
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Cancel
          </button>
          <PermissionGate permission="employee_management" action="create">
            <button
              type="submit"
              disabled={addEmployeeMutation.isPending || updateEmployeeMutation.isPending}
              className="px-6 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {(addEmployeeMutation.isPending || updateEmployeeMutation.isPending) ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Update Employee' : 'Create Employee'}
                </>
              )}
            </button>
          </PermissionGate>
        </div>
      </form>

      {/* Confirmation Dialog for Document Deletion */}
      <ConfirmationDialog
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({
          isOpen: false,
          fileToDelete: null,
          arrayType: null,
          arrayIndex: null
        })}
        onConfirm={() => {
          if (deleteConfirmModal.arrayType && deleteConfirmModal.arrayIndex !== null) {
            handleFileRemove(deleteConfirmModal.arrayType, deleteConfirmModal.arrayIndex);
          }
        }}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteConfirmModal.fileToDelete?.documentName || deleteConfirmModal.fileToDelete?.originalName || deleteConfirmModal.fileToDelete?.name || 'this document'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default AddEmployee;
